import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  addDoc,
  arrayUnion,
  getDocs,
  onSnapshot,
} from "firebase/firestore";
import { logBitacora, computeChanges } from '../utils/bitacora';
import { db } from "../firebase";
import { Button } from "../components/button";
import { Input } from "../components/input";
import { Textarea } from "../components/textarea";
import { Heading } from "../components/heading";
import { Text } from "../components/text";
import { Field, Label } from "../components/fieldset";
import { Dialog, DialogTitle, DialogBody, DialogActions } from "../components/dialog";
import { Checkbox } from "../components/checkbox";
import {
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentIcon,
  DocumentTextIcon,
  ArrowLeftIcon,
  EllipsisVerticalIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  UserIcon,
} from "@heroicons/react/24/solid";
import { ArchiveBoxIcon } from "@heroicons/react/24/outline";
import { useUser } from "../components/UserContext";
import { getDownloadURL, ref } from "firebase/storage";
import { storage } from "../firebase";
//import { ProcessedFileDialog } from '../components/ProcessedFileDialog';
import { ProcessedFileView } from '../components/ProcessedFileView';
import { ContractChatDialog } from '../components/ContractChatDialog';
import { UploadFianza } from '../components/UploadFianza';
import { FianzaView } from '../components/FianzaView';
import { reprocessFile } from '../api/documentUpload';
import ClientContratosYFianzas from '../components/ClientContratosYFianzas';
import ClientAfianzadoras from '../components/ClientAfianzadoras';
import ClientEstadisticas from '../components/ClientEstadisticas';
import { BitacoraTimeline } from '../components/BitacoraTimeline';
import AssignedUsersBubbles from '../components/AssignedUsersBubbles';
import UserAssignmentSelector from '../components/UserAssignmentSelector';
import { useClients, useData } from '../contexts/DataContext';
import InviteClientUserDialog from '../components/InviteClientUserDialog';
import { canInviteClientUsers } from '../utils/permissions';
import { computeDocumentExpiryStatus, getDocumentsNeedingAttention, parseDocumentDate, extractFieldValue } from '../utils/documentExpiry';

const REQUIRED_FILES = [
  { id: "actaConstitutiva", label: "Acta Constitutiva" },
  { id: "constanciaSituacionFiscal", label: "Constancia de Situación Fiscal" },
  { id: "actasAsambleas", label: "Actas de Asambleas" },
  { id: "poderes", label: "Poderes" },
  { id: "comprobanteDomicilio", label: "Comprobante de Domicilio" },
  {
    id: "identificacionOficial",
    label: "Identificación oficial del apoderado",
  },
  {
    id: "acuseReciboDeclaracion",
    label: "Acuse de Recibo de Declaración Anual",
  },
  { id: "declaracionEjercicio", label: "Declaración del Ejercicio" },
  {
    id: "estadosFinancierosExcel",
    label: "Estados Financieros Declaración Anual (Excel)",
  },
  {
    id: "estadosFinancieros",
    label: "Estados Financieros (Contador)",
  },
  {
    id: "estadosFinancierosParciales",
    label: "Estados Financieros Parciales (Contador)",
  },
  {
    id: "cedulaContador",
    label: "Cédula Profesional del Contador",
  },
  {
    id: "curriculumEmpresarial",
    label: "Currículum Empresarial",
  },
  { id: "contrato", label: "Contrato" },
];

// Short pill labels for the per-link history view (full labels are too long).
const REQUIRED_FILE_SHORT_LABELS = {
  actaConstitutiva: "Acta C.",
  constanciaSituacionFiscal: "CSF",
  actasAsambleas: "Actas",
  poderes: "Poderes",
  comprobanteDomicilio: "Domicilio",
  identificacionOficial: "ID",
  acuseReciboDeclaracion: "Acuse",
  declaracionEjercicio: "Decl.",
  estadosFinancierosExcel: "EEFF Excel",
  estadosFinancieros: "EEFF",
  estadosFinancierosParciales: "EEFF Parcial",
  cedulaContador: "Cédula",
  curriculumEmpresarial: "CV",
  contrato: "Contrato",
};

// Reduce a fileNames[] array to one of: procesado | en_proceso | error | pendiente.
const getLinkFileStatus = (fileNames) => {
  if (!Array.isArray(fileNames) || fileNames.length === 0) return "pendiente";
  if (fileNames.some((f) => f.status === "procesado")) return "procesado";
  if (fileNames.some((f) => f.status === "procesando" || f.status === "subido")) return "en_proceso";
  if (fileNames.some((f) => f.status === "error")) return "error";
  return "pendiente";
};

const LINK_PILL_STYLES = {
  procesado: { cls: "bg-verde-lima/20 text-azul-surelia border-verde-lima/40", icon: "✓" },
  en_proceso: { cls: "bg-amber-100 text-amber-700 border-amber-200", icon: "⏳" },
  error: { cls: "bg-red-100 text-red-700 border-red-200", icon: "✗" },
  pendiente: { cls: "bg-zinc-100 text-zinc-500 border-zinc-300 border-dashed", icon: "○" },
};

const LINK_PILL_STATUS_LABEL = {
  procesado: "Entregado",
  en_proceso: "En proceso",
  error: "Error al procesar",
  pendiente: "Pendiente",
};

// Mexicanized scientist names for fun link names
const SCIENTIST_FIRST_NAMES = [
  "Alberto", "Isaac", "María", "Carlitos", "Nikola", "Galileo", "Chayo",
  "Chucho", "Toño", "Beto", "Panchito", "Lupe", "Nachito", "Memo",
  "Pepe", "Tere", "Rigo", "Chema", "Lencho", "Güero", "Frida"
];

const SCIENTIST_LAST_NAMES = [
  "Einstein", "Curie", "Newton", "Tesla", "Hawking", "Darwin", "Galilei",
  "Pasteur", "Faraday", "Planck", "Schrödinger", "Heisenberg", "Lovelace",
  "Turing", "Feynman", "Bohr", "Mendel", "Lavoisier", "Kepler", "Copérnico"
];

const getRandomLinkName = () => {
  const firstName = SCIENTIST_FIRST_NAMES[Math.floor(Math.random() * SCIENTIST_FIRST_NAMES.length)];
  const lastName = SCIENTIST_LAST_NAMES[Math.floor(Math.random() * SCIENTIST_LAST_NAMES.length)];
  return `${firstName} ${lastName}`;
};

const TAB_LIST = [
  { key: "info", label: "Información General" },
  { key: "expediente", label: "Expediente Digital" },
  { key: "contratos", label: "Contratos y Fianzas" },
  { key: "estadisticas", label: "Estadísticas" },
  { key: "afianzadoras", label: "Afianzadoras" },
  { key: "bitacora", label: "Bitácora" },
];

// Legacy positional period keys ("0", "1", ...) always map to fiscal years 2024, 2023, ...
// (per user, the older Excel pipeline only ever ran on the 2024 declaración anual which covers 2024 + 2023).
const LEGACY_POSITIONAL_BASE_YEAR = 2024;
const labelForPeriod = (key) => {
  if (/^\d{4}$/.test(key)) return key;
  const n = Number(key);
  if (Number.isFinite(n)) return String(LEGACY_POSITIONAL_BASE_YEAR - n);
  return key;
};

// Collect period column keys from a financial_summary section.
// Newer entries key columns by 4-digit year ("2024"), older ones by positional index ("0", "1").
// Returns numeric-string keys sorted so the most recent period is first:
// years descending (2024, 2023), positional ascending (0 = most recent).
const collectPeriodKeys = (section) => {
  if (!section || typeof section !== 'object') return [];
  const keySet = new Set();
  Object.values(section).forEach((row) => {
    if (row && typeof row === 'object' && !Array.isArray(row)) {
      Object.keys(row).forEach((k) => { if (/^\d+$/.test(k)) keySet.add(k); });
    }
  });
  const keys = [...keySet];
  const isYearLike = keys.length > 0 && keys.every((k) => /^\d{4}$/.test(k));
  keys.sort((a, b) => isYearLike ? Number(b) - Number(a) : Number(a) - Number(b));
  return keys;
};

// Helper function to format financial values as currency
const formatFinancialValue = (key, value) => {
  
  // Handle null, undefined, or empty values
  if (value === null || value === undefined || value === '') {
    return '';
  }
  
  // Handle objects with value/sources structure
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    if (value.hasOwnProperty('value')) {
      // Extract the actual value from the object
      value = value.value;
    } else {
      // If it's an object without a value property, convert to string
      return JSON.stringify(value);
    }
  }
  
  // Handle arrays
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  
  // List of financial fields that should be formatted as currency
  const financialFields = [
    'totalActivo', 'totalPasivo', 'totalCapitalContable',
    'totalIngresos', 'totalGastos', 'totalUtilidadAntesImpuestos',
    'totalUtilidadNeta',
    'activoCirculante', 'pasivoCirculante', 'inventarios',
    'cuentasPorCobrar', 'cuentasPorPagar', 'costoDeVentas',
    'TotalIncome', 'TaxableIncome', 'TaxDue', 'TaxPayable',
  ];
  
  // Check if this is a financial field
  if (financialFields.includes(key)) {
    let numericValue;
    
    if (typeof value === 'string') {
      // Clean the string by removing commas, spaces, and currency symbols
      const cleanedValue = value.replace(/[,\s$]/g, '');
      numericValue = parseFloat(cleanedValue);
    } else {
      numericValue = value;
    }
    
    if (typeof numericValue === 'number' && !isNaN(numericValue)) {
      return new Intl.NumberFormat('es-MX', { 
        style: 'currency', 
        currency: 'MXN',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numericValue);
      }
    }
  
  // Also check for common financial field patterns in Excel data
  if (typeof value === 'string' || typeof value === 'number') {
    // Check if the key contains financial-related words
    const financialKeywords = ['monto', 'total', 'ingreso', 'gasto', 'utilidad', 'activo', 'pasivo', 'capital', 'impuesto', 'costo', 'precio', 'saldo', 'deuda', 'credito', 'inversion', 'ventas', 'isr', 'resultado_fiscal', 'activos_totales', 'cuentas_por_cobrar', 'cuentas_por_pagar', 'efectivo_equivalentes'];
    const keyLower = key.toLowerCase();
    
    if (financialKeywords.some(keyword => keyLower.includes(keyword))) {
      let numericValue;
      
      if (typeof value === 'string') {
        // Clean the string by removing commas, spaces, and currency symbols
        const cleanedValue = value.replace(/[,\s$]/g, '');
        numericValue = parseFloat(cleanedValue);
      } else {
        numericValue = value;
      }
      if (typeof numericValue === 'number' && !isNaN(numericValue)) {
        return new Intl.NumberFormat('es-MX', { 
          style: 'currency', 
          currency: 'MXN',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(numericValue);
      }
    }
  }
  // For non-financial fields or non-numeric values, return as string
  return String(value || '');
};

const ClienteDetails = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cliente, setCliente] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedClient, setEditedClient] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFileDialogOpen, setIsFileDialogOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState({});
  const [linksData, setLinksData] = useState({});
  const [copiedLinkId, setCopiedLinkId] = useState(null);
  const [linkName, setLinkName] = useState("");
  const [reprocessingFiles, setReprocessingFiles] = useState(new Set());
  const [isAssignmentDialogOpen, setIsAssignmentDialogOpen] = useState(false);
  const [isInviteClientUserOpen, setIsInviteClientUserOpen] = useState(false);
  const { user, userData } = useUser();
  const { updateClientAssignedUsers } = useClients();
  const { clientsList } = useData();
  const calculateFinancialRatios = (financialData) => {
    const ratios = {};

    const getNumericValue = (key) => {
      const value = financialData[key]?.value ?? financialData[key];
      if (value === null || value === undefined || value === '') return null;
      if (typeof value === 'string') {
        const parsed = parseFloat(value.replace(/[,\s$]/g, ''));
        return isNaN(parsed) ? null : parsed;
      }
      return typeof value === 'number' ? value : null;
    };

    const totalActivo = getNumericValue('totalActivo');
    const totalPasivo = getNumericValue('totalPasivo');
    const totalCapitalContable = getNumericValue('totalCapitalContable');
    const totalIngresos = getNumericValue('totalIngresos');
    const totalUtilidadAntesImpuestos = getNumericValue('totalUtilidadAntesImpuestos');
    const totalUtilidadNeta = getNumericValue('totalUtilidadNeta');
    const activoCirculante = getNumericValue('activoCirculante');
    const pasivoCirculante = getNumericValue('pasivoCirculante');
    const inventarios = getNumericValue('inventarios');
    const cuentasPorCobrar = getNumericValue('cuentasPorCobrar');
    const cuentasPorPagar = getNumericValue('cuentasPorPagar');
    const costoDeVentas = getNumericValue('costoDeVentas');

    // Liquidez Corriente = activo circulante / pasivo circulante
    if (activoCirculante !== null && pasivoCirculante !== null && pasivoCirculante > 0) {
      ratios.liquidez = activoCirculante / pasivoCirculante;
    }

    // Prueba Ácida = (activo circulante − inventarios) / pasivo circulante
    if (
      activoCirculante !== null &&
      inventarios !== null &&
      pasivoCirculante !== null &&
      pasivoCirculante > 0
    ) {
      ratios.pruebaAcida = (activoCirculante - inventarios) / pasivoCirculante;
    }

    // Apalancamiento (Debt Ratio) = pasivo total / activo total
    if (totalActivo !== null && totalPasivo !== null && totalActivo > 0) {
      ratios.apalancamiento = totalPasivo / totalActivo;
    }

    // Capital de Trabajo = activo circulante − pasivo circulante (puede ser negativo)
    if (activoCirculante !== null && pasivoCirculante !== null) {
      ratios.capitalTrabajo = activoCirculante - pasivoCirculante;
    }

    // ROE = utilidad neta / capital contable × 100 (puede ser negativo)
    if (totalCapitalContable !== null && totalUtilidadNeta !== null && totalCapitalContable > 0) {
      ratios.roe = (totalUtilidadNeta / totalCapitalContable) * 100;
    }

    // ROA = utilidad neta / activo total × 100 (etiquetado "ROI" por compatibilidad de UI)
    if (totalActivo !== null && totalUtilidadNeta !== null && totalActivo > 0) {
      ratios.roi = (totalUtilidadNeta / totalActivo) * 100;
    }

    // Margen de Utilidad Neta = utilidad neta / ingresos × 100
    if (totalIngresos !== null && totalUtilidadNeta !== null && totalIngresos > 0) {
      ratios.margenUtilidad = (totalUtilidadNeta / totalIngresos) * 100;
    }

    // Rendimiento del Capital = utilidad antes de impuestos / capital contable × 100
    if (
      totalCapitalContable !== null &&
      totalUtilidadAntesImpuestos !== null &&
      totalCapitalContable > 0
    ) {
      ratios.rendimientoCapital = (totalUtilidadAntesImpuestos / totalCapitalContable) * 100;
    }

    // Rentabilidad Bruta (Margen Bruto) = (ingresos − costo de ventas) / ingresos × 100
    if (totalIngresos !== null && costoDeVentas !== null && totalIngresos > 0) {
      ratios.rentabilidadBruta = ((totalIngresos - costoDeVentas) / totalIngresos) * 100;
    }

    // Días de Inventario = (inventarios / costo de ventas) × 365
    if (inventarios !== null && costoDeVentas !== null && costoDeVentas > 0) {
      ratios.diasInventario = (inventarios / costoDeVentas) * 365;
    }

    // Días CXC = (cuentas por cobrar / ingresos) × 365
    if (cuentasPorCobrar !== null && totalIngresos !== null && totalIngresos > 0) {
      ratios.diasCXC = (cuentasPorCobrar / totalIngresos) * 365;
    }

    // Días CXP = (cuentas por pagar / costo de ventas) × 365
    if (cuentasPorPagar !== null && costoDeVentas !== null && costoDeVentas > 0) {
      ratios.diasCXP = (cuentasPorPagar / costoDeVentas) * 365;
    }

    // Ciclo Económico = días inventario + días CXC − días CXP
    if (
      ratios.diasInventario !== undefined &&
      ratios.diasCXC !== undefined &&
      ratios.diasCXP !== undefined
    ) {
      ratios.cicloEconomico = ratios.diasInventario + ratios.diasCXC - ratios.diasCXP;
    }

    return ratios;
  };
  
  // Get active tab from URL or default to "info"
  const getActiveTabFromURL = () => {
    const tabFromURL = searchParams.get('tab');
    const validTabs = TAB_LIST.map(tab => tab.key);
    return validTabs.includes(tabFromURL) ? tabFromURL : "info";
  };
  
  const [activeTab, setActiveTab] = useState(getActiveTabFromURL());
  
  // Function to handle tab changes and update URL
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('tab', newTab);
      return newParams;
    });
  };
  
  const [expandedDocs, setExpandedDocs] = useState({});
  const [hoveredTooltip, setHoveredTooltip] = useState(null);
  const actionMenuRefs = useRef({});
  const downloadUrlsRef = useRef({});
  //const [isProcessedDialogOpen, setIsProcessedDialogOpen] = useState(false);
  //const [processedDialogData, setProcessedDialogData] = useState({});
  const [isProcessedViewOpen, setIsProcessedViewOpen] = useState(false);
  const [processedViewData, setProcessedViewData] = useState({});
  const [isChatDialogOpen, setIsChatDialogOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [expandedProcessed, setExpandedProcessed] = useState({});
  // null = follow the recency-based default; otherwise an explicit ratios source
  // ('estadosFinancieros' | 'estadosFinancierosParciales') chosen via the toggle.
  const [ratiosSource, setRatiosSource] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);
  const [alertType, setAlertType] = useState('success'); // 'success' or 'error'
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, message: '', onConfirm: null, title: '' });

  const isReprocessing = reprocessingFiles.size > 0;

  // Prevent browser reload/close while reprocessing
  useEffect(() => {
    if (!isReprocessing) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isReprocessing]);

  // Block browser back/forward navigation while reprocessing
  useEffect(() => {
    if (!isReprocessing) return;
    // Push a duplicate entry so pressing back stays on the same page
    window.history.pushState(null, '', window.location.href);
    const handler = () => {
      window.history.pushState(null, '', window.location.href);
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, [isReprocessing]);

  // Contracts section state
  // Add state for UploadFianza dialog
  const [isUploadFianzaOpen, setIsUploadFianzaOpen] = useState(false);
  const [uploadFianzaContractId, setUploadFianzaContractId] = useState(null);
  // Add state for FianzaView
  const [isFianzaViewOpen, setIsFianzaViewOpen] = useState(false);
  const [selectedFianzaView, setSelectedFianzaView] = useState(null);

  // Function to mark file as reviewed (cotejar)
  const handleMarkAsReviewed = async (linkId, fileDocId) => {
    try {
      const fileDocRef = doc(db, "links", linkId, "files", fileDocId);
      await updateDoc(fileDocRef, {
        reviewedBy: user?.email || user?.uid, // Use email if available, fallback to uid
        reviewedAt: new Date(),
      });
      
      // Show success alert
      setAlertMessage("Archivo marcado como revisado exitosamente");
      setAlertType('success');
      setTimeout(() => setAlertMessage(null), 3000);
      
      // Refresh data to show updated status
      await fetchClienteAndLinks();
    } catch (error) {
      console.error("Error marking file as reviewed:", error);
      setAlertMessage("Error al marcar archivo como revisado");
      setAlertType('error');
      setTimeout(() => setAlertMessage(null), 3000);
    }
  };

  // Function to unmark file as reviewed (descotejar)
  const handleUnmarkAsReviewed = async (linkId, fileDocId) => {
    try {
      const fileDocRef = doc(db, "links", linkId, "files", fileDocId);
      await updateDoc(fileDocRef, {
        reviewedBy: null,
        reviewedAt: null,
      });
      
      // Show success alert
      setAlertMessage("Revisión cancelada exitosamente");
      setAlertType('success');
      setTimeout(() => setAlertMessage(null), 3000);
      
      // Refresh data to show updated status
      await fetchClienteAndLinks();
    } catch (error) {
      console.error("Error unmarking file as reviewed:", error);
      setAlertMessage("Error al remover revisión");
      setAlertType('error');
      setTimeout(() => setAlertMessage(null), 3000);
    }
  };

  // Set up real-time listeners for file updates
  const setupFileListeners = useCallback((linkIds) => {
    const unsubscribers = [];

    linkIds.forEach(linkId => {
      const filesCollectionRef = collection(db, "links", linkId, "files");
      const unsubscribe = onSnapshot(filesCollectionRef, (snapshot) => {
        const allFiles = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // Update the specific link's files in linksData
        setLinksData(prevLinksData => {
          const updatedLinksData = { ...prevLinksData };
          if (updatedLinksData[linkId]) {
            updatedLinksData[linkId] = {
              ...updatedLinksData[linkId],
              allFiles: allFiles
            };
          }
          return updatedLinksData;
        });
      }, (error) => {
        console.error(`Error listening to files for link ${linkId}:`, error);
      });
      
      unsubscribers.push(unsubscribe);
    });

    return unsubscribers;
  }, []);

  const fetchClienteAndLinks = useCallback(async () => {
    try {
      const clienteDoc = await getDoc(doc(db, "clientes", clientId));
      if (clienteDoc.exists()) {
        const clienteData = {
          id: clienteDoc.id,
          ...clienteDoc.data(),
          links: clienteDoc.data().links || [],
          contracts: clienteDoc.data().contracts || [],
        };
        setCliente(clienteData);
        setEditedClient(clienteData);

        // Fetch details for each link and their files from subcollection
        const linksPromises = clienteData.links.map(async (linkId) => {
          const linkDoc = await getDoc(doc(db, "links", linkId));
          if (!linkDoc.exists()) return null;

          const filesCollectionRef = collection(db, "links", linkId, "files");
          const filesSnapshot = await getDocs(filesCollectionRef);

          const allFiles = filesSnapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }));
          
          return {
              id: linkId,
              ...linkDoc.data(),
              allFiles: allFiles,
          };
        });

        const resolvedLinks = (await Promise.all(linksPromises)).filter(Boolean);
        const linkDataMap = resolvedLinks.reduce((acc, link) => {
            acc[link.id] = link;
            return acc;
        }, {});

        setLinksData(linkDataMap);

        // Clean up existing file listeners
        fileListenersRef.current.forEach(unsubscribe => unsubscribe());
        fileListenersRef.current = [];

        // Set up real-time listeners for file updates
        if (clienteData.links.length > 0) {
          fileListenersRef.current = setupFileListeners(clienteData.links);
        }

        // Contracts are now fetched via DataContext and used in ClientContratosYFianzas component
      } else {
        navigate("/app/clientes");
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching client details:", error);
      setIsLoading(false);
    }
  }, [clientId, navigate, setupFileListeners]);

  // Store unsubscribers to clean up on unmount
  const fileListenersRef = useRef([]);

  // Handle file reprocessing
  const handleReprocessFile = async (fileData, fileId) => {
    // Ask for confirmation first using custom dialog
    const fileLabel = REQUIRED_FILES.find(f => f.id === fileId)?.label || fileId;

    setConfirmDialog({
      isOpen: true,
      title: 'Confirmar reprocesamiento',
      message: `¿Estás seguro de que deseas reprocesar "${fileLabel}"? Esto volverá a analizar el documento y reescribirá los datos existentes. Esta operacion no se puede deshacer.`,
      onConfirm: async () => {
        setConfirmDialog({ isOpen: false, message: '', onConfirm: null, title: '' });
        await executeReprocess(fileData, fileId);
      }
    });
  };

  const executeReprocess = async (fileData, fileId) => {
    const linkId = fileData.linkId;
    // Extract timestamp from the id field (format: "fileType_timestamp")
    const uploadedAt = fileData.raw.id.split('_')[1];
    const reprocessKey = `${linkId}_${fileId}_${uploadedAt}`;

    console.log('Debug - fileData:', fileData);
    console.log('Debug - fileData.raw:', fileData.raw);
    console.log('Debug - extracted uploadedAt from id:', uploadedAt);
    console.log('Reprocessing file with params:', { clientId, linkId, fileId, uploadedAt });

    // Validate all required fields
    if (!clientId || !linkId || !fileId || !uploadedAt) {
      console.error('Missing required fields:', { clientId, linkId, fileId, uploadedAt });
      setAlertMessage('Error: Faltan campos requeridos para reprocesar el archivo');
      setAlertType('error');
      setTimeout(() => setAlertMessage(null), 5000);
      return;
    }
    
    try {
      setReprocessingFiles(prev => new Set([...prev, reprocessKey]));
      
      const result = await reprocessFile(
        clientId,
        linkId,
        fileId,
        uploadedAt
      );
      
      if (result.status === 'success') {
        // Show success message (real-time listeners will update the data automatically)
        setAlertMessage("Archivo reprocesado exitosamente");
        setAlertType('success');
        setTimeout(() => setAlertMessage(null), 3000);
      } else {
        throw new Error(result.error || 'Error reprocessing file');
      }
    } catch (error) {
      console.error('Error reprocessing file:', error);
      setAlertMessage(`Error al reprocesar archivo: ${error.message}`);
      setAlertType('error');
      setTimeout(() => setAlertMessage(null), 5000);
    } finally {
      setReprocessingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(reprocessKey);
        return newSet;
      });
    }
  };

  useEffect(() => {
    fetchClienteAndLinks();
  }, [fetchClienteAndLinks]);

  // Cleanup listeners on unmount
  useEffect(() => {
    return () => {
      fileListenersRef.current.forEach(unsubscribe => unsubscribe());
    };
  }, []);

  // Sync activeTab state when URL changes (for browser back/forward)
  useEffect(() => {
    const tabFromURL = getActiveTabFromURL();
    if (tabFromURL !== activeTab) {
      setActiveTab(tabFromURL);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedClient((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const clienteRef = doc(db, "clientes", clientId);

      // Filter out system fields from update
      const {
        id,
        createdAt,
        links,
        usuario,           // Legacy field
        org,               // Don't update org
        createdBy,         // Don't update creator
        assignedUsers,     // Managed separately
        contracts,         // Managed separately
        ...updateData
      } = editedClient;

      // Get current data BEFORE update for change tracking
      const currentClientDoc = await getDoc(clienteRef);
      const currentData = currentClientDoc.data();

      // Perform the update
      await updateDoc(clienteRef, updateData);

      // Track which fields we monitor for changes
      const fieldsToTrack = [
        'status',
        'statusDescription',
        'nombreEmpresa',
        'nombreContacto',
        'correoContacto',
        'telefonoContacto',
        'notas',
      ];

      // Compute actual changes
      const changes = computeChanges(
        currentData,
        { ...currentData, ...updateData },
        fieldsToTrack
      );

      // Log each change separately for better audit trail
      for (const change of changes) {
        let description;

        // Generate specific description for this field
        if (change.field === 'status') {
          const statusMap = {
            green: 'verde',
            orange: 'naranja',
            red: 'rojo',
            gray: 'gris',
          };
          description = `Actualizó estado del cliente a '${statusMap[change.newValue] || change.newValue}'`;
        } else if (change.field === 'statusDescription') {
          description = 'Actualizó descripción del estado del cliente';
        } else if (change.field === 'notas') {
          const oldNota = change.oldValue || '(vacío)';
          const newNota = change.newValue || '(vacío)';
          description = `Actualizó notas del cliente. Anterior: "${oldNota}" → Nueva: "${newNota}"`;
        } else if (change.field === 'nombreEmpresa') {
          description = `Actualizó nombre del cliente a '${change.newValue}'`;
        } else if (change.field === 'nombreContacto') {
          description = `Actualizó nombre de contacto del cliente a '${change.newValue}'`;
        } else if (change.field === 'correoContacto') {
          description = `Actualizó correo de contacto del cliente a '${change.newValue}'`;
        } else if (change.field === 'telefonoContacto') {
          description = `Actualizó teléfono de contacto del cliente a '${change.newValue}'`;
        } else {
          description = `Actualizó ${change.field} del cliente`;
        }

        // Log this individual change (non-blocking)
        try {
          await logBitacora({
            action: 'UPDATE',
            collection: 'clientes',
            documentId: clientId,
            changes: [change], // Single change per log entry
            metadata: {
              clientId,
              clientName: updateData.nombreEmpresa || currentData.nombreEmpresa || 'Sin nombre',
              description,
            },
            user,
            userData,
          });
        } catch (logError) {
          console.error('Failed to log client update to bitacora:', logError);
        }
      }

      // Update local state
      setCliente(editedClient);
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating client:", error);
    }
  };

  const handleFileSelection = (fileId) => {
    setSelectedFiles((prev) => ({
      ...prev,
      [fileId]: !prev[fileId],
    }));
  };

  const handleGenerateLink = async () => {
    if (!user) {
      navigate("/login");
      return;
    }

    try {
      // Create complete files object with all files as objects with status, counters y fileNames
      const completeFiles = REQUIRED_FILES.reduce(
        (acc, file) => ({
          ...acc,
          [file.id]: {
            uploadedCount: 0,
            processedCount: 0,
            required: !!selectedFiles[file.id],
            fileNames: [],
          }
        }),
        {}
      );

      // Create new document in links collection
      const linkData = {
        clientId,
        createdAt: new Date(),
        createdBy: user?.uid,
        files: completeFiles,
        status: "pending",
        name: linkName.trim() || `Link ${(cliente.links?.length || 0) + 1}`,
        // Org-based access: use client's org, fallback to user's org
        ...(cliente.org && { org: cliente.org }),
        ...(!cliente.org && userData?.org && { org: userData.org }),
      };
      const linkDoc = await addDoc(collection(db, "links"), linkData);

      // Update client document with new link ID
      const clienteRef = doc(db, "clientes", clientId);
      await updateDoc(clienteRef, {
        links: arrayUnion(linkDoc.id),
      });

      // Log link creation (non-blocking)
      try {
        await logBitacora({
          action: 'CREATE',
          collection: 'links',
          documentId: linkDoc.id,
          metadata: {
            clientId: clientId,
            clientName: cliente.nombreEmpresa || 'Sin nombre',
            linkId: linkDoc.id,
            description: `Creó enlace de expediente '${linkData.name}'`,
          },
          user,
          userData,
        });
      } catch (logError) {
        console.error('Failed to log link creation:', logError);
      }

      // Update local state
      setCliente((prev) => ({
        ...prev,
        links: [...(prev.links || []), linkDoc.id],
      }));

      setIsFileDialogOpen(false);
      setSelectedFiles({});
      setLinkName(""); // Reset link name
    } catch (error) {
      console.error("Error generating link:", error);
    }
  };

  const handleCopyLink = async (linkId) => {
    const linkUrl = `${window.location.origin}/expediente/${linkId}`;

    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(linkUrl);
        setCopiedLinkId(linkId);
      } else {
        alert("Tu navegador no soporta la función de copiar al portapapeles.");
      }
    } catch (err) {
      console.error("Failed to copy/share link:", err);
      alert(
        "No se pudo copiar el link automáticamente. Por favor, cópialo manualmente."
      );
    }

    // Always clear the copied state after 2 seconds
    setTimeout(() => setCopiedLinkId(null), 1500);
  };

  // Nueva función para abrir el diálogo con selección personalizada
  const handleOpenFileDialogWithSelection = (fileIds = null) => {
    let selection;
    if (fileIds && Array.isArray(fileIds)) {
      selection = REQUIRED_FILES.reduce(
        (acc, file) => ({
          ...acc,
          [file.id]: fileIds.includes(file.id),
        }),
        {}
      );
    } else {
      // Todos seleccionados por default
      selection = REQUIRED_FILES.reduce(
      (acc, file) => ({
        ...acc,
        [file.id]: true,
      }),
      {}
    );
    }
    setSelectedFiles(selection);
    setLinkName("");
    setIsFileDialogOpen(true);
  };

  // Cerrar menú de acciones al hacer click fuera
  useEffect(() => {
    function handleClickOutside(event) {
      Object.entries(actionMenuRefs.current).forEach(([fileId, ref]) => {
        if (ref && !ref.contains(event.target)) {
          setExpandedDocs(prev => ({ ...prev, [fileId + '_menu']: false }));
        }
      });
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!cliente) {
    return null;
  }

  // --- RESUMEN GENERAL DE LINKS PENDIENTES ---
  const pendingLinks = (cliente.links || [])
    .map((linkId) => {
      const link = linksData[linkId];
      if (!link) {
        return null;
      }

      // Get file types that are required for this link
      const requiredFileTypes = Object.entries(link.files || {})
        .filter(([_, fileConfig]) => fileConfig.required)
        .map(([fileId, _]) => fileId);

      // Get file types that have been provided (non-error status)
      const providedFileTypes = new Set(
        (link.allFiles || [])
          .filter(f => ['subido', 'procesando', 'procesado'].includes(f.status))
          .map(f => f.fileType)
      );
      
      // Check which standard documents are pending (excluding contract)
      const pendingDocs = requiredFileTypes.filter(
        requiredType => requiredType !== 'contrato' && !providedFileTypes.has(requiredType)
      );

      // Also check if a contract was required and is still pending
      const contractIsPending = requiredFileTypes.includes('contrato') && !providedFileTypes.has('contrato');

      if (pendingDocs.length > 0 || contractIsPending) {
        return { 
          ...link, 
          id: linkId, 
          pendingDocs,
          contractIsPending 
        };
      }

      return null;
    })
    .filter(Boolean);

  const pendingLinksSection = pendingLinks.length > 0 ? (
    <div className="mb-6 p-4 bg-orange-50 border mt-4 border-orange-200 rounded-lg">
      <Text className="font-semibold text-orange-800 mb-3">Tienes {pendingLinks.length} link(s) con archivos pendientes:</Text>
      <div className="flex flex-col gap-4">
        {pendingLinks.map((link) => (
          <div key={link.id} className="bg-white p-3 rounded-lg border border-orange-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
              <span className="font-medium text-orange-700 text-sm truncate max-w-[200px] sm:max-w-none" title={link.name || link.id}>{link.name || link.id}</span>
              <button
                className="self-start sm:self-center p-2 rounded hover:bg-orange-100 transition border border-orange-300"
                onClick={() => window.open(`/app/expedientes/${link.id}`, '_blank')}
                title="Abrir link de carga"
                type="button"
              >
                <ArrowTopRightOnSquareIcon className="w-4 h-4 text-orange-700" />
              </button>
            </div>
            {/* Missing files list */}
            <div className="text-xs text-orange-600">
              <span className="font-medium">Archivos faltantes:</span>
              <div className="mt-1 flex flex-wrap gap-1">
                {link.pendingDocs.map((fileId) => {
                  const fileLabel = REQUIRED_FILES.find(f => f.id === fileId)?.label || fileId;
                  return (
                    <span 
                      key={fileId}
                      className="inline-block px-2 py-1 bg-orange-200 text-orange-800 rounded text-xs"
                    >
                      {fileLabel}
                    </span>
                  );
                })}
                {link.contractIsPending && (
                  <span className="inline-block px-2 py-1 bg-orange-200 text-orange-800 rounded text-xs">
                    Contrato
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  ) : null;

  // --- RESUMEN DE DOCUMENTOS PROCESADOS (INFORMACIÓN GENERAL) ---
  // Agrupar procesados por tipo de archivo y ordenarlos por fecha (más reciente primero)
  const processedByFile = {};
  Object.values(linksData).forEach(link => {
    if (!link.files || !link.allFiles) return;

    // Iterate through required file types in the link's file map
    Object.keys(link.files).forEach(fileType => {
      const fileConfig = link.files[fileType];
      if (!fileConfig || !fileConfig.fileNames) return;

      const processedFileNames = fileConfig.fileNames.filter(f => f.status === 'procesado' || f.status === 'procesando');

      processedFileNames.forEach(f_name => {
        // Find the corresponding full file data from the subcollection dump
        const subcollectionDocId = `${fileType}_${f_name.uploadedAt}`;
        const fullFileData = link.allFiles.find(af => af.id === subcollectionDocId);

        if (fullFileData) {
          if (!processedByFile[fileType]) {
            processedByFile[fileType] = [];
          }
          processedByFile[fileType].push({
            fileId: fileType,
            linkId: link.id,
            linkName: link.name || link.id,
            data: fullFileData.results || null, // Allow null results for procesando status
            raw: fullFileData,
            createdAt: fullFileData.processedAt ? new Date(fullFileData.processedAt) : new Date(f_name.uploadedAt),
          });
        }
      });
    });
  });

  // --- ANÁLISIS FINANCIERO (RATIOS) ---
  // Two possible sources feed the ratios: the annual "Estados Financieros (Contador)"
  // and the interim "Estados Financieros Parciales". Both extract the same figure
  // fields, so calculateFinancialRatios works unchanged for either. A toggle in the
  // Razones Financieras card lets the user switch; default = whichever covers the
  // more recent period.
  const financialAnalysis = {};

  const buildFinancialEntry = (fileId, periodLabelFn) => {
    const docs = processedByFile[fileId] || [];
    if (docs.length === 0) return;
    const mostRecent = docs.sort((a, b) => b.createdAt - a.createdAt)[0];
    financialAnalysis[fileId] = {
      data: mostRecent.data,
      linkName: mostRecent.linkName,
      createdAt: mostRecent.createdAt,
      periodLabel: periodLabelFn(mostRecent.data),
      ratios: calculateFinancialRatios(mostRecent.data),
    };
  };

  // Annual: period label from the fiscal year.
  buildFinancialEntry('estadosFinancieros', (data) => {
    const year = extractFieldValue(data, 'fiscalYear');
    return year ? String(year) : null;
  });
  // Partial: period label from periodoInicio – periodoFin.
  buildFinancialEntry('estadosFinancierosParciales', (data) => {
    const inicio = extractFieldValue(data, 'periodoInicio');
    const fin = extractFieldValue(data, 'periodoFin');
    if (inicio && fin) return `${inicio} – ${fin}`;
    return fin || inicio || null;
  });

  // Effective date per source (later period wins as the default). Fall back to
  // upload createdAt when a period date is missing/unparseable.
  const anualEntry = financialAnalysis.estadosFinancieros;
  const parcialEntry = financialAnalysis.estadosFinancierosParciales;
  const anualEffectiveDate = anualEntry
    ? (() => {
        const year = extractFieldValue(anualEntry.data, 'fiscalYear');
        const parsed = year ? parseDocumentDate(`31-12-${year}`) : null;
        return parsed || new Date(anualEntry.createdAt || 0);
      })()
    : null;
  const parcialEffectiveDate = parcialEntry
    ? (parseDocumentDate(extractFieldValue(parcialEntry.data, 'periodoFin')) ||
       new Date(parcialEntry.createdAt || 0))
    : null;

  let defaultRatiosSource = null;
  if (parcialEntry && anualEntry) {
    defaultRatiosSource = (parcialEffectiveDate > anualEffectiveDate)
      ? 'estadosFinancierosParciales'
      : 'estadosFinancieros';
  } else if (parcialEntry) {
    defaultRatiosSource = 'estadosFinancierosParciales';
  } else if (anualEntry) {
    defaultRatiosSource = 'estadosFinancieros';
  }

  const activeRatiosSource = (ratiosSource && financialAnalysis[ratiosSource])
    ? ratiosSource
    : defaultRatiosSource;
  const activeFinancials = activeRatiosSource ? financialAnalysis[activeRatiosSource] : null;
  

  // --- DOCUMENTOS QUE REQUIEREN ATENCIÓN (VENCIDOS / POR VENCER) ---
  const documentsNeedingAttention = getDocumentsNeedingAttention(processedByFile, REQUIRED_FILES);

  const FIELD_LABELS_MAP = {
    actaConstitutiva: [
      { key: 'CompanyName', label: 'Razón Social' },
      { key: 'DateOfCompanyConstitution', label: 'Fecha Constitución' },
      { key: 'AdministrationPower', label: 'Poderes Administrativos' },
      { key: 'DomainPowers', label: 'Poderes de Dominio' },
      { key: 'BothPowers', label: 'Ambos Poderes' },
      { key: 'ListOfShareholders', label: 'Accionistas' },
      { key: 'ShareholderStock', label: '% Accionistas' },
      { key: 'ForigenersExclusionClause', label: 'Exclusión de Extranjeros' },
    ],
    actasAsambleas: [
      { key: 'CompanyName', label: 'Razón Social' },
      { key: 'DateOfCompanyConstitution', label: 'Fecha Constitución' },
      { key: 'AdministrationPower', label: 'Poderes Administrativos' },
      { key: 'DomainPowers', label: 'Poderes de Dominio' },
      { key: 'BothPowers', label: 'Ambos Poderes' },
      { key: 'ListOfShareholders', label: 'Accionistas' },
      { key: 'ShareholderStock', label: '% Accionistas' },
      { key: 'ForigenersExclusionClause', label: 'Exclusión de Extranjeros' },
      { key: 'NotarizationDate', label: 'Fecha de Notarización' },
    ],
    constanciaSituacionFiscal: [
      { key: 'RFC', label: 'RFC' },
      { key: 'CompanyLegalName', label: 'Nombre Legal' },
      { key: 'CapitalRegime', label: 'Régimen Capital' },
      { key: 'TaxRegime', label: 'Régimen Fiscal' },
      { key: 'FiscalAddress', label: 'Domicilio Fiscal' },
      { key: 'PostalCode', label: 'Código Postal' },
      { key: 'EconomicActivity', label: 'Actividad Económica' },
      { key: 'RegistrationDate', label: 'Fecha de Inscripción' },
      { key: 'Status', label: 'Status' },
    ],
    comprobanteDomicilio: [
      { key: 'CompanyName', label: 'Nombre de la Compañía' },
      { key: 'ServiceAddress', label: 'Dirección de Servicio' },
      { key: 'PostalCode', label: 'Código Postal' },
      { key: 'DocumentType', label: 'Tipo de Documento' },
      { key: 'DocumentDate', label: 'Fecha del Documento' },
    ],
    identificacionOficial: [
      { key: 'DocumentType', label: 'Tipo de Documento' },
      { key: 'FullName', label: 'Nombre Completo' },
      { key: 'ExpirationDate', label: 'Fecha de Vencimiento' },
      { key: 'DocumentNumber', label: 'Número de Documento' },
      { key: 'IsValid', label: 'Es Válido' },
    ],
    acuseReciboDeclaracion: [
      { key: 'PresentationDate', label: 'Fecha de Presentación' },
      { key: 'ReceiptNumber', label: 'Folio de Recepción' },
      { key: 'RFC', label: 'RFC' },
      { key: 'CompanyName', label: 'Razón Social' },
      { key: 'DeclarationType', label: 'Tipo de Declaración' },
      { key: 'FiscalYear', label: 'Ejercicio Fiscal' },
    ],
    declaracionEjercicio: [
      { key: 'RFC', label: 'RFC' },
      { key: 'CompanyName', label: 'Razón Social' },
      { key: 'FiscalYear', label: 'Ejercicio Fiscal' },
      { key: 'TotalIncome', label: 'Ingresos Totales' },
      { key: 'TaxableIncome', label: 'Utilidad Fiscal' },
      { key: 'TaxDue', label: 'Impuesto Causado' },
      { key: 'TaxPayable', label: 'Impuesto a Cargo' },
    ],
    estadosFinancieros: [
      { key: 'totalActivo', label: 'Total Activo' },
      { key: 'activoCirculante', label: 'Activo Circulante' },
      { key: 'inventarios', label: 'Inventarios' },
      { key: 'cuentasPorCobrar', label: 'Cuentas por Cobrar' },
      { key: 'totalPasivo', label: 'Total Pasivo' },
      { key: 'pasivoCirculante', label: 'Pasivo Circulante' },
      { key: 'cuentasPorPagar', label: 'Cuentas por Pagar' },
      { key: 'totalCapitalContable', label: 'Total Capital Contable' },
      { key: 'totalIngresos', label: 'Total Ingresos' },
      { key: 'costoDeVentas', label: 'Costo de Ventas' },
      { key: 'totalGastos', label: 'Total Gastos' },
      { key: 'totalUtilidadAntesImpuestos', label: 'Total Utilidad Antes de Impuestos' },
      { key: 'totalUtilidadNeta', label: 'Total Utilidad Neta' },
      { key: 'fiscalYear', label: 'Año Fiscal' },
      { key: 'hasAccountantSignature', label: 'Firma del Contador' },
      { key: 'hasContadorCedulaProfesional', label: 'Cédula Profesional del Contador' },
      { key: 'hasAccountantResponsabilityText', label: 'Texto de Responsabilidad del Contador' },
    ],
    estadosFinancierosParciales: [
      { key: 'periodoInicio', label: 'Periodo (Inicio)' },
      { key: 'periodoFin', label: 'Periodo (Fin)' },
      { key: 'totalActivo', label: 'Total Activo' },
      { key: 'activoCirculante', label: 'Activo Circulante' },
      { key: 'inventarios', label: 'Inventarios' },
      { key: 'cuentasPorCobrar', label: 'Cuentas por Cobrar' },
      { key: 'totalPasivo', label: 'Total Pasivo' },
      { key: 'pasivoCirculante', label: 'Pasivo Circulante' },
      { key: 'cuentasPorPagar', label: 'Cuentas por Pagar' },
      { key: 'totalCapitalContable', label: 'Total Capital Contable' },
      { key: 'totalIngresos', label: 'Total Ingresos' },
      { key: 'costoDeVentas', label: 'Costo de Ventas' },
      { key: 'totalGastos', label: 'Total Gastos' },
      { key: 'totalUtilidadAntesImpuestos', label: 'Total Utilidad Antes de Impuestos' },
      { key: 'totalUtilidadNeta', label: 'Total Utilidad Neta' },
      { key: 'fiscalYear', label: 'Año Fiscal' },
      { key: 'hasAccountantSignature', label: 'Firma del Contador' },
      { key: 'hasContadorCedulaProfesional', label: 'Cédula Profesional del Contador' },
      { key: 'hasAccountantResponsabilityText', label: 'Texto de Responsabilidad del Contador' },
    ],
    cedulaContador: [
      { key: 'NombreCompleto', label: 'Nombre Completo' },
      { key: 'NumeroCedula', label: 'Número de Cédula' },
      { key: 'CarreraOTitulo', label: 'Carrera o Título' },
      { key: 'Institucion', label: 'Institución' },
      { key: 'FechaExpedicion', label: 'Fecha de Expedición' },
      { key: 'LugarExpedicion', label: 'Lugar de Expedición' },
      { key: 'TipoDocumento', label: 'Tipo de Documento' },
      { key: 'URLVerificacion', label: 'URL de Verificación' },
    ],
    curriculumEmpresarial: [
      { key: 'NombreEmpresa', label: 'Nombre de la Empresa' },
      { key: 'Resumen', label: 'Resumen' },
      { key: 'ServiciosPrincipales', label: 'Servicios Principales' },
      { key: 'ExperienciaDestacada', label: 'Experiencia Destacada' },
      { key: 'EquipoClave', label: 'Equipo Clave' },
      { key: 'AniosExperiencia', label: 'Años de Experiencia' },
      { key: 'Ubicacion', label: 'Ubicación' },
    ],
    poderes: [
      { key: 'CompanyName', label: 'Razón Social' },
      { key: 'Poderdante', label: 'Poderdante' },
      { key: 'AdministrationPower', label: 'Poderes Administrativos' },
      { key: 'DomainPowers', label: 'Poderes de Dominio' },
      { key: 'BothPowers', label: 'Ambos Poderes' },
      { key: 'NotaryName', label: 'Nombre del Notario' },
      { key: 'NotaryNumber', label: 'Número del Notario' },
      { key: 'EscrituraNumber', label: 'Número de Escritura' },
      { key: 'NotarizationDate', label: 'Fecha de Notarización' },
      { key: 'ExpirationDate', label: 'Fecha de Vencimiento' },
    ],
  };
  const cards = Object.entries(processedByFile)
    .filter(([fileId]) => fileId !== 'estadosFinancierosExcel' && fileId !== 'contrato')
    // Ordenar alfabéticamente por label
    .sort(([fileIdA], [fileIdB]) => {
      const labelA = REQUIRED_FILES.find(f => f.id === fileIdA)?.label || fileIdA;
      const labelB = REQUIRED_FILES.find(f => f.id === fileIdB)?.label || fileIdB;
      return labelA.localeCompare(labelB, 'es');
    })
    .map(([fileId, versions], idx) => {
      // Ordenar por fecha descendente
      const sorted = versions.sort((a, b) => b.createdAt - a.createdAt);
      const mostRecent = sorted[0];
      const showHistory = expandedProcessed[fileId];
      const fieldDefs = FIELD_LABELS_MAP[fileId] || Object.keys(mostRecent.data).map(k => ({ key: k, label: k }));
      
      // Check if this file is currently being reprocessed
      const reprocessKey = `${mostRecent.linkId}_${fileId}_${mostRecent.raw.id.split('_')[1]}`;
      const isCurrentlyReprocessing = reprocessingFiles.has(reprocessKey);
      const cardExpiryStatus = mostRecent.data ? computeDocumentExpiryStatus(fileId, mostRecent.data) : null;


      return (
        <div key={fileId} className="bg-white border border-azul-surelia/20 rounded-lg shadow-sm p-4 mb-4 flex flex-col h-full">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-azul-surelia">{REQUIRED_FILES.find(f => f.id === fileId)?.label || fileId}</span>
              {isCurrentlyReprocessing && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 animate-pulse">
                  Reprocesando...
                </span>
              )}
              {cardExpiryStatus && cardExpiryStatus.status === 'expired' && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700" title={cardExpiryStatus.message}>
                  Vencido
                </span>
              )}
              {cardExpiryStatus && cardExpiryStatus.status === 'warning' && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700" title={cardExpiryStatus.message}>
                  Por vencer
                </span>
              )}
            </div>
            <div className="flex items-center justify-between text-xs text-zinc-500">
              <span className="truncate max-w-[150px]" title={mostRecent.linkName}>
                {mostRecent.linkName}
              </span>
              {mostRecent.createdAt && (
                <span className="text-zinc-400 ml-2 flex-shrink-0">{mostRecent.createdAt.toLocaleDateString("es-MX", { year: "numeric", month: "short", day: "numeric" })}</span>
              )}
            </div>
          </div>
          {isCurrentlyReprocessing ? (
            <div className="flex-grow space-y-3">
              <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded shimmer-animation"></div>
              <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded shimmer-animation" style={{animationDelay: '0.2s'}}></div>
              <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded shimmer-animation w-3/4" style={{animationDelay: '0.4s'}}></div>
              <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded shimmer-animation w-1/2" style={{animationDelay: '0.6s'}}></div>
            </div>
          ) : mostRecent.raw?.reviewedBy ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 flex-grow">
              {fieldDefs.map(({ key, label }) => (
                mostRecent.data[key] && (
                  <div key={key} className="flex flex-col mb-2">
                    <span className="text-xs text-azul-surelia/70 font-semibold">{label}</span>
                    <span className="text-sm text-gray-800 whitespace-pre-line">{formatFinancialValue(key, typeof mostRecent.data[key].value === 'string' || typeof mostRecent.data[key].value === 'number' ? mostRecent.data[key].value : mostRecent.data[key]?.value || mostRecent.data[key] || '')}</span>
                  </div>
                )
              ))}
            </div>
          ) : (
            <div className="flex-grow flex items-center justify-center py-8">
              <div className="text-center">
                <p className="text-sm text-gray-600 font-medium">Documento pendiente de revisión</p>
                <p className="text-xs text-gray-500 mt-1">Los datos se mostrarán una vez revisado</p>
              </div>
            </div>
          )}
          

          
          {sorted.length > 1 && (
            <button
              className="text-xs text-azul-surelia underline hover:text-azul-surelia/80 mt-2"
              onClick={() => setExpandedProcessed(prev => ({ ...prev, [fileId]: !prev[fileId] }))}
              type="button"
            >
              {showHistory ? 'Ocultar historial' : `Ver historial (${sorted.length - 1})`}
            </button>
          )}
          {showHistory && sorted.slice(1).map((version, vIdx) => (
            <div key={vIdx} className="mt-2 border-t pt-2">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-zinc-500 truncate max-w-[120px]" title={version.linkName}>{version.linkName}</span>
                <span className="text-xs text-zinc-400">{version.createdAt.toLocaleDateString("es-MX", { year: "numeric", month: "short", day: "numeric" })}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                {fieldDefs.map(({ key, label }) => (
                  version.data[key] && (
                    <div key={key} className="flex flex-col mb-1">
                      <span className="text-xs text-zinc-500 font-semibold">{label}</span>
                      <span className="text-sm text-zinc-800 whitespace-pre-line">{formatFinancialValue(key, typeof version.data[key].value === 'string' || typeof version.data[key].value === 'number' ? version.data[key].value : version.data[key]?.value || version.data[key] || '')}</span>
                    </div>
                  )
                ))}
              </div>
            </div>
          ))}
          {/* Bottom section with indicator and Ver button */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-auto pt-3 border-t border-zinc-200 gap-3 sm:gap-2">
            <div className="flex items-center gap-2 min-w-0">
              {mostRecent.raw?.reviewedBy ? (
                <div className="flex flex-col gap-1 min-w-0">
                  <span 
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium"
                    title={`Revisado ${mostRecent.raw?.reviewedAt ? 'el ' + new Date(mostRecent.raw.reviewedAt.seconds * 1000).toLocaleDateString("es-MX") : ''} por ${mostRecent.raw.reviewedBy}`}
                  >
                    ✓ Revisado
                  </span>
                  <span className="text-xs text-zinc-600 truncate">
                    por {mostRecent.raw.reviewedBy}
                    {mostRecent.raw?.reviewedAt && (
                      <span className="block truncate">
                        el {new Date(mostRecent.raw.reviewedAt.seconds * 1000).toLocaleDateString("es-MX", { 
                          year: "numeric", 
                          month: "short", 
                          day: "numeric" })}</span>
                    )}
                  </span>
                </div>
              ) : (
                <span 
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-medium"
                  title="No revisado"
                >
                  ✗ No revisado
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                size="xs"
                color="surelia"
                onClick={async () => {
                  let pdfUrl = null;
                  const filePath = mostRecent.raw?.filePath || mostRecent.raw?.path;
                  if (filePath) {
                    try {
                      // Check if URL is already cached
                      if (downloadUrlsRef.current[filePath]) {
                        pdfUrl = downloadUrlsRef.current[filePath];
                      } else {
                        // Get download URL from Firebase Storage
                        const fileRef = ref(storage, filePath);
                        pdfUrl = await getDownloadURL(fileRef);
                        downloadUrlsRef.current[filePath] = pdfUrl;
                      }
                    } catch (e) {
                      console.error('Error getting PDF URL:', e);
                    }
                  }
                  
                  setProcessedViewData({
                    processedFileData: {
                      ...mostRecent.raw,
                      fileDocId: mostRecent.raw.id // Use full ID as fileDocId (e.g., "actaConstitutiva_1750388755183")
                    },
                    fileType: fileId,
                    fileName: mostRecent.raw?.name || (mostRecent.raw?.filePath?.split('/').pop()) || mostRecent.raw?.fileName || 'Documento',
                    fileLabel: REQUIRED_FILES.find(f => f.id === fileId)?.label || fileId,
                    pdfUrl,
                    linkId: mostRecent.linkId, // Add linkId for review functionality
                  });
                  setIsProcessedViewOpen(true);
                }}
              >
                Ver
              </Button>
              <Button
                size="xs"
                color="surelia"
                outline
                disabled={reprocessingFiles.has(`${mostRecent.linkId}_${fileId}_${mostRecent.raw.id.split('_')[1]}`)}
                onClick={() => handleReprocessFile(mostRecent, fileId)}
              >
                {reprocessingFiles.has(`${mostRecent.linkId}_${fileId}_${mostRecent.raw.id.split('_')[1]}`) ? 'Reprocesando...' : 'Reprocesar'}
              </Button>
            </div>
          </div>
        </div>
      );
    });
  const excelSummaries = processedByFile['estadosFinancierosExcel'] || [];
  const summarySection = (Object.keys(processedByFile).length === 0) ? null : (
    <div className="mt-8 mb-4">
      <Heading level={3} className="text-lg font-bold mb-4 text-azul-surelia">Resumen de Documentos Procesados</Heading>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
        {cards}
        {excelSummaries.length > 0 && (
          <div className="col-span-full">
            <div className="bg-white border border-azul-surelia/20 rounded-lg shadow-sm p-4">
              {(() => {
                const mostRecentExcel = excelSummaries.sort((a, b) => b.createdAt - a.createdAt)[0];
                const excelReprocessKey = `${mostRecentExcel.linkId}_estadosFinancierosExcel_${mostRecentExcel.raw.id.split('_')[1]}`;
                const isExcelReprocessing = reprocessingFiles.has(excelReprocessKey);
                
                return (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-azul-surelia">Estados Financieros (Excel)</span>
                        {isExcelReprocessing && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 animate-pulse">
                            Reprocesando...
                          </span>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="xs"
                          color="surelia"
                          outline
                          disabled={isExcelReprocessing}
                          onClick={() => handleReprocessFile(mostRecentExcel, 'estadosFinancierosExcel')}
                        >
                          {isExcelReprocessing ? 'Reprocesando...' : 'Reprocesar'}
                        </Button>
                      </div>
                    </div>
                    {isExcelReprocessing ? (
                      <div className="space-y-4">
                        <div className="h-6 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded shimmer-animation"></div>
                        <div className="h-32 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded shimmer-animation" style={{animationDelay: '0.2s'}}></div>
                        <div className="h-24 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded shimmer-animation" style={{animationDelay: '0.4s'}}></div>
                        <div className="h-20 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded shimmer-animation" style={{animationDelay: '0.6s'}}></div>
                      </div>
                    ) : (
                      <>
                        {/* Render a table for each sheet if present */}
                        {(() => {
                          const sortedExcelSummaries = excelSummaries.sort((a, b) => b.createdAt - a.createdAt);
                          const showExcelHistory = expandedProcessed['estadosFinancierosExcel'];
                          const summariesToShow = showExcelHistory ? sortedExcelSummaries : [sortedExcelSummaries[0]];
                          
                          return (
                            <>
                              {summariesToShow.map((summary, idx) => {
                                return (
                <div key={idx} className={`mb-4 ${idx > 0 ? 'pt-4 border-t border-zinc-200 bg-zinc-50/50 p-3 rounded' : ''}`}>
                  {/* Show timestamp for historical entries */}
                  {idx > 0 && (
                    <div className="mb-3 text-xs text-zinc-500">
                      Procesado: {summary.createdAt.toLocaleDateString("es-MX", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </div>
                  )}
                  {summary.data?.sheetData && Object.entries(summary.data.sheetData).map(([sheetName, rows]) => (
                    <div key={sheetName} className="mb-3">
                      <span className="text-sm font-semibold text-azul-surelia/70">{sheetName}</span>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs border border-zinc-200 rounded-lg mt-1">
                          <thead className="bg-zinc-100">
                            <tr>
                              {rows[0] && Object.keys(rows[0]).map((header) => (
                                <th key={header} className="py-1 px-2 text-left font-semibold text-zinc-600">{header}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((row, rowIdx) => (
                              <tr key={rowIdx} className="even:bg-zinc-50">
                                {Object.entries(row).map(([key, value], cellIdx) => (
                                  <td key={cellIdx} className="py-1 px-2 text-zinc-700">
                                    {typeof value === 'object' ? JSON.stringify(value) : formatFinancialValue(key, value) ?? ''}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                  {/* Balance General */}
                  {summary.data?.financial_summary?.balance_general && (() => {
                    const availableYears = collectPeriodKeys(summary.data.financial_summary.balance_general);
                    if (availableYears.length === 0) return null;

                    return (
                      <div className="mt-2">
                        <span className="text-xs font-semibold text-zinc-600">Balance General</span>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border border-zinc-200 rounded-lg mt-1">
                            <thead className="bg-zinc-100">
                              <tr>
                                <th className="py-1 px-2 text-left font-semibold text-zinc-600">Concepto</th>
                                {availableYears.map(year => (
                                  <th key={year} className="py-1 px-2 text-right font-semibold text-zinc-600">{labelForPeriod(year)}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(summary.data.financial_summary.balance_general).map(([key, value]) => (
                                <tr key={key} className="even:bg-zinc-50">
                                  <td className="py-1 px-2 text-zinc-700 font-medium capitalize">{key.split('_').join(' ')}</td>
                                  {availableYears.map(year => (
                                    <td key={year} className="py-1 px-2 text-zinc-700 text-right">{formatFinancialValue(key, value[year] || 0)}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}
                  {/* Estado de Resultados */}
                  {summary.data?.financial_summary?.estado_resultados && (() => {
                    const availableYears = collectPeriodKeys(summary.data.financial_summary.estado_resultados);
                    if (availableYears.length === 0) return null;

                    return (
                      <div className="mt-2">
                        <span className="text-xs font-semibold text-zinc-600">Estado de Resultados</span>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border border-zinc-200 rounded-lg mt-1">
                            <thead className="bg-zinc-100">
                              <tr>
                                <th className="py-1 px-2 text-left font-semibold text-zinc-600">Concepto</th>
                                {availableYears.map(year => (
                                  <th key={year} className="py-1 px-2 text-right font-semibold text-zinc-600">{labelForPeriod(year)}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(summary.data.financial_summary.estado_resultados).map(([key, value]) => (
                                <tr key={key} className="even:bg-zinc-50">
                                  <td className="py-1 px-2 text-zinc-700 font-medium capitalize">{key.split('_').join(' ')}</td>
                                  {availableYears.map(year => (
                                    <td key={year} className="py-1 px-2 text-zinc-700 text-right">{formatFinancialValue(key, value[year] || 0)}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}
                  {/* Conciliación Fiscal */}
                  {summary.data?.financial_summary?.conciliacion_fiscal && (() => {
                    const availableYears = collectPeriodKeys(summary.data.financial_summary.conciliacion_fiscal);
                    if (availableYears.length === 0) return null;

                    return (
                      <div className="mt-2">
                        <span className="text-xs font-semibold text-zinc-600">Conciliación Fiscal</span>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border border-zinc-200 rounded-lg mt-1">
                            <thead className="bg-zinc-100">
                              <tr>
                                <th className="py-1 px-2 text-left font-semibold text-zinc-600">Concepto</th>
                                {availableYears.map(year => (
                                  <th key={year} className="py-1 px-2 text-right font-semibold text-zinc-600">{labelForPeriod(year)}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(summary.data.financial_summary.conciliacion_fiscal).map(([key, value]) => (
                                <tr key={key} className="even:bg-zinc-50">
                                  <td className="py-1 px-2 text-zinc-700 font-medium capitalize">{key.split('_').join(' ')}</td>
                                  {availableYears.map(year => (
                                    <td key={year} className="py-1 px-2 text-zinc-700 text-right">{formatFinancialValue(key, value[year] || 0)}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}
                </div>
                              );
                              })}

                              {/* History toggle button for Excel */}
                              {sortedExcelSummaries.length > 1 && (
                                <button
                                  className="text-xs text-azul-surelia underline hover:text-azul-surelia/80 mt-4"
                                  onClick={() => setExpandedProcessed(prev => ({ ...prev, estadosFinancierosExcel: !prev.estadosFinancierosExcel }))}
                                  type="button"
                                >
                                  {showExcelHistory ? 'Ocultar historial' : `Ver historial (${sortedExcelSummaries.length - 1})`}
                                </button>
                              )}
                            </>
                          );
                        })()}
                      </>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Alert notification */}
      {alertMessage && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
          alertType === 'success' 
            ? 'bg-green-100 border border-green-200 text-green-800' 
            : 'bg-red-100 border border-red-200 text-red-800'
        }`}>
          <div className="flex items-center gap-2">
            {alertType === 'success' ? (
              <span className="text-green-600">✓</span>
            ) : (
              <span className="text-red-600">✗</span>
            )}
            <span className="font-medium">{alertMessage}</span>
          </div>
        </div>
      )}
      
      {/* Archived banner */}
      {cliente.archived && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ArchiveBoxIcon className="w-5 h-5 text-amber-600" />
            <Text className="font-semibold text-amber-800">
              Este cliente está archivado. No aparece en las vistas principales.
            </Text>
          </div>
        </div>
      )}

      {/* Header: Back button and company name */}
      <div className="flex items-center gap-3 mb-4">
        <Button 
          color="surelia" 
          outline 
          onClick={() => {
            const backState = location.state;
            const paramsFromState = backState?.fromContratosFianzas ? {
              targetPath: backState.targetPath || (backState.viewMode === "contratos" ? "/app/contratos" : "/app/fianzas"),
              page: backState.page || 1,
              perPage: backState.perPage || 5,
            } : null;

            const pageParam = searchParams.get("page");
            const perPageParam = searchParams.get("perPage");
            const paramsFromQuery = (pageParam || perPageParam) ? {
              targetPath: null,
              page: pageParam || "1",
              perPage: perPageParam || "5",
            } : null;

            const chosen = paramsFromState || paramsFromQuery;
            if (chosen) {
              const targetPath = chosen.targetPath || "/app/fianzas";
              const params = new URLSearchParams({
                page: chosen.page,
                perPage: chosen.perPage,
              });
              navigate(`${targetPath}?${params.toString()}`);
              return;
            }
            navigate("/app/clientes");
          }} 
          size="sm"
        >
          <ArrowLeftIcon className="h-4 w-4" />
        </Button>
              <Heading level={1} className="text-3xl font-bold text-azul-surelia">
                {cliente.nombreEmpresa}
              </Heading>
              {canInviteClientUsers(userData) && (
                <Button
                  color="surelia"
                  outline
                  size="sm"
                  onClick={() => setIsInviteClientUserOpen(true)}
                  className="ml-auto"
                >
                  <UserIcon className="h-4 w-4 mr-1" />
                  Invitar Cliente
                </Button>
              )}
            </div>
      {/* Tabs Navigation */}
      <div className="overflow-x-auto mb-6 border-b border-azul-surelia/20">
        <div className="flex gap-1 min-w-max">
          {TAB_LIST.map((tab) => (
            <button
              key={tab.key}
              className={`px-3 sm:px-6 py-3 font-semibold focus:outline-none transition-all duration-200 border-b-2 whitespace-nowrap text-sm sm:text-base ${
                activeTab === tab.key
                  ? "text-azul-surelia border-azul-surelia bg-azul-surelia/5"
                  : "text-gray-500 border-transparent hover:text-azul-surelia hover:border-azul-surelia/40 hover:bg-azul-surelia/5"
              }`}
              onClick={() => handleTabChange(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      {/* Tab Panels */}
        <div className="bg-white rounded-lg shadow">
        {activeTab === "info" && (
          <div className="p-0">
            {/* Información General (migrar UI existente aquí) */}
            {isEditing ? (
              <div className="bg-white rounded-lg shadow-lg p-6 border border-azul-surelia/20">
                <div className="mb-6">
                  <Heading level={2} className="text-2xl font-bold text-azul-surelia mb-2">Editar Información</Heading>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field className="flex items-center gap-4">
                    <Label className="text-azul-surelia font-semibold w-32 flex-shrink-0">Empresa</Label>
                    <Input
                      name="nombreEmpresa"
                      value={editedClient.nombreEmpresa}
                      onChange={handleInputChange}
                      required
                      autoComplete="off"
                      className="border-azul-surelia/30 focus:border-azul-surelia focus:ring-azul-surelia/20 flex-1"
                    />
                  </Field>
                  <Field className="flex items-center gap-4">
                    <Label className="text-azul-surelia font-semibold w-32 flex-shrink-0">RFC</Label>
                    <Input
                      name="rfc"
                      value={editedClient.rfc || ""}
                      onChange={handleInputChange}
                      placeholder="XAXX010101000"
                      maxLength="13"
                      autoComplete="off"
                      className="uppercase border-azul-surelia/30 focus:border-azul-surelia focus:ring-azul-surelia/20 flex-1"
                    />
                  </Field>
                  <Field className="flex items-center gap-4">
                    <Label className="text-azul-surelia font-semibold w-32 flex-shrink-0">Contacto</Label>
                    <Input
                      name="nombreContacto"
                      value={editedClient.nombreContacto}
                      onChange={handleInputChange}
                      required
                      autoComplete="off"
                      className="border-azul-surelia/30 focus:border-azul-surelia focus:ring-azul-surelia/20 flex-1"
                    />
                  </Field>
                  <Field className="flex items-center gap-4">
                    <Label className="text-azul-surelia font-semibold w-32 flex-shrink-0">Teléfono</Label>
                    <Input
                      type="tel"
                      name="telefonoContacto"
                      value={editedClient.telefonoContacto}
                      onChange={handleInputChange}
                      required
                      autoComplete="off"
                      className="border-azul-surelia/30 focus:border-azul-surelia focus:ring-azul-surelia/20 flex-1"
                    />
                  </Field>
                  <Field className="flex items-center gap-4 md:col-span-2">
                    <Label className="text-azul-surelia font-semibold w-32 flex-shrink-0">Correo</Label>
                    <Input
                      type="email"
                      name="correoContacto"
                      value={editedClient.correoContacto}
                      onChange={handleInputChange}
                      required
                      autoComplete="off"
                      className="border-azul-surelia/30 focus:border-azul-surelia focus:ring-azul-surelia/20 flex-1"
                    />
                  </Field>
                </div>
                <Field>
                  <Label className="text-azul-surelia font-semibold">Notas</Label>
                  <Textarea
                    name="notas"
                    value={editedClient.notas}
                    onChange={handleInputChange}
                    rows={3}
                    autoComplete="off"
                    className="border-azul-surelia/30 focus:border-azul-surelia focus:ring-azul-surelia/20"
                  />
                </Field>
                <div className="flex justify-end space-x-3 pt-4">
                  <Button
                    outline
                    color="surelia"
                    onClick={() => {
                      setIsEditing(false);
                      setEditedClient(cliente);
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" color="surelia">Guardar</Button>
                </div>
                </form>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-lg p-6 border border-azul-surelia/20">
                <div className="flex justify-between items-center mb-6">
                  <Heading level={2} className="text-2xl font-bold text-azul-surelia">Información General</Heading>
                  <Button
                    onClick={() => setIsEditing(true)}
                    disabled={isEditing}
                    size="sm"
                    color="surelia"
                  >
                    Editar Información
                  </Button>
                </div>
                <table className="w-full text-sm border border-azul-surelia/30 rounded-lg overflow-hidden shadow-sm">
                  <tbody>
                    <tr className="even:bg-azul-surelia/5">
                      <td className="font-semibold text-azul-surelia py-3 px-4 w-48 border-r border-azul-surelia/20">Empresa</td>
                      <td className="py-3 px-4 text-gray-700 font-medium">{cliente.nombreEmpresa}</td>
                    </tr>
                    <tr className="even:bg-azul-surelia/5">
                      <td className="font-semibold text-azul-surelia py-3 px-4 border-r border-azul-surelia/20">RFC</td>
                      <td className="py-3 px-4">
                        {cliente.rfc ? (
                          <span className="inline-flex items-center px-2 py-1 rounded bg-azul-surelia/10 text-azul-surelia font-mono text-sm">
                            {cliente.rfc}
                          </span>
                        ) : (
                          <span className="text-gray-400 italic">--</span>
                        )}
                      </td>
                    </tr>
                    <tr className="even:bg-azul-surelia/5">
                      <td className="font-semibold text-azul-surelia py-3 px-4 border-r border-azul-surelia/20">Contacto</td>
                      <td className="py-3 px-4 text-gray-700 font-medium">{cliente.nombreContacto}</td>
                    </tr>
                    <tr className="even:bg-azul-surelia/5">
                      <td className="font-semibold text-azul-surelia py-3 px-4 border-r border-azul-surelia/20">Correo</td>
                      <td className="py-3 px-4 text-gray-700 font-medium">{cliente.correoContacto}</td>
                    </tr>
                    <tr className="even:bg-azul-surelia/5">
                      <td className="font-semibold text-azul-surelia py-3 px-4 border-r border-azul-surelia/20">Teléfono</td>
                      <td className="py-3 px-4 text-cian-digital font-medium">{cliente.telefonoContacto}</td>
                    </tr>
                    <tr className="even:bg-azul-surelia/5">
                      <td className="font-semibold text-azul-surelia py-3 px-4 border-r border-azul-surelia/20">Fecha de Alta</td>
                      <td className="py-3 px-4 text-gray-700 font-medium">{cliente.createdAt?.toDate().toLocaleDateString("es-MX", { year: "numeric", month: "long", day: "numeric" })}</td>
                    </tr>
                    {cliente.notas && (
                      <tr className="even:bg-azul-surelia/5">
                        <td className="font-semibold text-azul-surelia py-3 px-4 align-top border-r border-azul-surelia/20">Notas</td>
                        <td className="py-3 px-4 whitespace-pre-line text-gray-700">{cliente.notas}</td>
                      </tr>
                    )}
                    <tr className="even:bg-azul-surelia/5">
                      <td className="font-semibold text-azul-surelia py-3 px-4 border-r border-azul-surelia/20">Responsables</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <AssignedUsersBubbles
                            userIds={cliente.assignedUsers || []}
                            maxDisplay={5}
                            size="md"
                          />
                          <Button
                            outline
                            color="surelia"
                            className="text-sm"
                            onClick={() => setIsAssignmentDialogOpen(true)}
                          >
                            Gestionar
                          </Button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
                {pendingLinksSection}

                {/* Documents Needing Attention (Expired / Expiring Soon) */}
                {documentsNeedingAttention.length > 0 && (
                  <div className="mt-6 p-4 rounded-lg border border-amber-200 bg-amber-50">
                    <div className="flex items-center gap-2 mb-2">
                      <ExclamationTriangleIcon className="w-5 h-5 text-amber-600" />
                      <span className="font-semibold text-amber-800">
                        Documentos que requieren atención ({documentsNeedingAttention.length})
                      </span>
                    </div>
                    <ul className="space-y-1">
                      {documentsNeedingAttention.map(doc => (
                        <li key={doc.fileId} className="text-sm text-amber-700">
                          <span className={`font-medium ${doc.status === 'expired' ? 'text-red-700' : 'text-amber-700'}`}>
                            {doc.label}
                          </span>
                          : {doc.message}
                        </li>
                      ))}
                    </ul>
                    <Button
                      size="sm"
                      color="surelia"
                      outline
                      className="mt-3"
                      onClick={() => handleOpenFileDialogWithSelection(
                        documentsNeedingAttention.map(d => d.fileId)
                      )}
                    >
                      Solicitar actualización
                    </Button>
                  </div>
                )}

                {/* Financial Ratios Section */}
                {activeFinancials && (
                  <div className="mt-8 mb-6">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                      <div className="flex items-center gap-3">
                        <Heading level={3} className="text-lg font-bold text-azul-surelia">Razones Financieras</Heading>
                        {activeFinancials.periodLabel && (
                          <span className="text-xs font-medium text-azul-surelia/70 bg-azul-surelia/5 px-2 py-0.5 rounded">
                            {activeRatiosSource === 'estadosFinancierosParciales' ? 'Parcial' : 'Anual'}: {activeFinancials.periodLabel}
                          </span>
                        )}
                      </div>
                      {anualEntry && parcialEntry && (
                        <div className="inline-flex rounded-lg border border-azul-surelia/20 p-0.5 bg-azul-surelia/5">
                          <button
                            type="button"
                            onClick={() => setRatiosSource('estadosFinancierosParciales')}
                            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${activeRatiosSource === 'estadosFinancierosParciales' ? 'bg-azul-surelia text-white' : 'text-azul-surelia/70 hover:text-azul-surelia'}`}
                          >
                            Parcial
                          </button>
                          <button
                            type="button"
                            onClick={() => setRatiosSource('estadosFinancieros')}
                            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${activeRatiosSource === 'estadosFinancieros' ? 'bg-azul-surelia text-white' : 'text-azul-surelia/70 hover:text-azul-surelia'}`}
                          >
                            Anual
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="bg-white border border-azul-surelia/20 rounded-lg shadow-sm p-6">
                      {activeFinancials && (
                        <div className="space-y-6">
                          {/* Liquidez y Solvencia */}
                          <div>
                            <h4 className="text-sm font-bold text-azul-surelia mb-3 border-b border-azul-surelia/20 pb-1">Liquidez y Solvencia</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                              <div className="bg-azul-surelia/5 p-3 rounded-lg min-w-0">
                                <span className="text-xs text-azul-surelia/70 font-medium block">Liquidez</span>
                                <span className="text-base sm:text-lg font-bold text-gray-800 truncate block">
                                  {activeFinancials.ratios.liquidez && !isNaN(activeFinancials.ratios.liquidez) 
                                    ? activeFinancials.ratios.liquidez.toFixed(2)
                                    : "N/A"
                                  }
                                </span>
                              </div>
                              <div className="bg-azul-surelia/5 p-3 rounded-lg min-w-0">
                                <span className="text-xs text-azul-surelia/70 font-medium block">Prueba Ácida</span>
                                <span className="text-base sm:text-lg font-bold text-gray-800 truncate block">
                                  {activeFinancials.ratios.pruebaAcida && !isNaN(activeFinancials.ratios.pruebaAcida) 
                                    ? activeFinancials.ratios.pruebaAcida.toFixed(2)
                                    : "N/A"
                                  }
                                </span>
                              </div>
                              <div className="bg-azul-surelia/5 p-3 rounded-lg min-w-0">
                                <span className="text-xs text-azul-surelia/70 font-medium block">Apalancamiento</span>
                                <span className="text-base sm:text-lg font-bold text-gray-800 truncate block">
                                  {activeFinancials.ratios.apalancamiento && !isNaN(activeFinancials.ratios.apalancamiento) 
                                    ? (activeFinancials.ratios.apalancamiento * 100).toFixed(1) + "%"
                                    : "N/A"
                                  }
                                </span>
                              </div>
                              <div className="bg-azul-surelia/5 p-3 rounded-lg min-w-0">
                                <span className="text-xs text-azul-surelia/70 font-medium block">Capital de Trabajo</span>
                                <span className="text-base sm:text-lg font-bold text-gray-800 truncate block">
                                  {activeFinancials.ratios.capitalTrabajo !== undefined && !isNaN(activeFinancials.ratios.capitalTrabajo) 
                                    ? formatFinancialValue('capitalTrabajo', activeFinancials.ratios.capitalTrabajo)
                                    : "N/A"
                                  }
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Rentabilidad */}
                          <div>
                            <h4 className="text-sm font-bold text-azul-surelia mb-3 border-b border-azul-surelia/20 pb-1">Rentabilidad</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                              <div className="bg-verde-lima/10 p-3 rounded-lg min-w-0">
                                <span className="text-xs text-azul-surelia/70 font-medium block">ROE</span>
                                <span className="text-base sm:text-lg font-bold text-gray-800 truncate block">
                                  {activeFinancials.ratios.roe != null && !isNaN(activeFinancials.ratios.roe)
                                    ? activeFinancials.ratios.roe.toFixed(1) + "%"
                                    : "N/A"
                                  }
                                </span>
                              </div>
                              <div className="bg-verde-lima/10 p-3 rounded-lg min-w-0">
                                <span className="text-xs text-azul-surelia/70 font-medium block">ROI</span>
                                <span className="text-base sm:text-lg font-bold text-gray-800 truncate block">
                                  {activeFinancials.ratios.roi != null && !isNaN(activeFinancials.ratios.roi)
                                    ? activeFinancials.ratios.roi.toFixed(1) + "%"
                                    : "N/A"
                                  }
                                </span>
                              </div>
                              <div className="bg-verde-lima/10 p-3 rounded-lg min-w-0">
                                <span className="text-xs text-azul-surelia/70 font-medium block">Margen Utilidad</span>
                                <span className="text-base sm:text-lg font-bold text-gray-800 truncate block">
                                  {activeFinancials.ratios.margenUtilidad != null && !isNaN(activeFinancials.ratios.margenUtilidad)
                                    ? activeFinancials.ratios.margenUtilidad.toFixed(1) + "%"
                                    : "N/A"
                                  }
                                </span>
                              </div>
                              <div className="bg-verde-lima/10 p-3 rounded-lg min-w-0">
                                <span className="text-xs text-azul-surelia/70 font-medium block">Rentabilidad Bruta</span>
                                <span className="text-base sm:text-lg font-bold text-gray-800 truncate block">
                                  {activeFinancials.ratios.rentabilidadBruta != null && !isNaN(activeFinancials.ratios.rentabilidadBruta)
                                    ? activeFinancials.ratios.rentabilidadBruta.toFixed(1) + "%"
                                    : "N/A"
                                  }
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Ciclos Operativos */}
                          <div>
                            <h4 className="text-sm font-bold text-azul-surelia mb-3 border-b border-azul-surelia/20 pb-1">Ciclos Operativos</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                              <div className="bg-cian-digital/10 p-3 rounded-lg min-w-0">
                                <span className="text-xs text-azul-surelia/70 font-medium block">Días Inventario</span>
                                <span className="text-base sm:text-lg font-bold text-gray-800 truncate block">
                                  {activeFinancials.ratios.diasInventario && !isNaN(activeFinancials.ratios.diasInventario) 
                                    ? activeFinancials.ratios.diasInventario.toFixed(0)
                                    : "N/A"
                                  }
                                </span>
                              </div>
                              <div className="bg-cian-digital/10 p-3 rounded-lg min-w-0">
                                <span className="text-xs text-azul-surelia/70 font-medium block">Días CXC</span>
                                <span className="text-base sm:text-lg font-bold text-gray-800 truncate block">
                                  {activeFinancials.ratios.diasCXC && !isNaN(activeFinancials.ratios.diasCXC) 
                                    ? activeFinancials.ratios.diasCXC.toFixed(0)
                                    : "N/A"
                                  }
                                </span>
                              </div>
                              <div className="bg-cian-digital/10 p-3 rounded-lg min-w-0">
                                <span className="text-xs text-azul-surelia/70 font-medium block">Días CXP</span>
                                <span className="text-base sm:text-lg font-bold text-gray-800 truncate block">
                                  {activeFinancials.ratios.diasCXP && !isNaN(activeFinancials.ratios.diasCXP) 
                                    ? activeFinancials.ratios.diasCXP.toFixed(0)
                                    : "N/A"
                                  }
                                </span>
                              </div>
                              <div className="bg-cian-digital/10 p-3 rounded-lg border-2 border-azul-surelia/30 min-w-0">
                                <span className="text-xs text-azul-surelia/70 font-medium block">Ciclo Económico</span>
                                <span className="text-base sm:text-lg font-bold text-azul-surelia truncate block">
                                  {activeFinancials.ratios.cicloEconomico != null && !isNaN(activeFinancials.ratios.cicloEconomico)
                                    ? activeFinancials.ratios.cicloEconomico.toFixed(0)
                                    : "N/A"
                                  }
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {summarySection}
              </div>
            )}
          </div>
        )}
        {activeTab === "expediente" && (
          <div className="p-6">
            <div className="mb-6">
              <Heading level={2} className="text-2xl font-bold text-azul-surelia mb-2">Expediente Digital</Heading>
            </div>
            <div className="mb-6">
              <Text className="text-lg font-semibold text-azul-surelia mb-4">Documentos requeridos</Text>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {REQUIRED_FILES.filter(file => file.id !== 'contrato').map((file) => {
                  // 1. Find all "processed" files for this file type across all links by checking the fileNames array.
                  const processedFiles = (cliente.links || [])
                    .map((linkId) => {
                      const link = linksData[linkId];
                      if (!link || !link.files?.[file.id]?.fileNames || !link.allFiles) return null;

                      const processedFileNames = link.files[file.id].fileNames.filter(f => f.status === 'procesado');

                      return processedFileNames.map(f_name => {
                        // Find the corresponding full file data from the subcollection dump
                        const subcollectionDocId = `${file.id}_${f_name.uploadedAt}`;
                        const fullFileData = link.allFiles.find(af => af.id === subcollectionDocId);
                        
                        return {
                          ...(fullFileData || {}), // Subcollection data first
                          ...f_name, // f_name (from fileNames array) wins — has correct PDF path/name
                          fileType: file.id,
                          linkId,
                          linkName: link.name || linkId,
                        };
                      });
                    })
                    .filter(Boolean)
                    .flat();
                  
                  // 2. Sort by uploaded date to find the most recent.
                  processedFiles.sort((a, b) => (b.uploadedAt || 0) - (a.uploadedAt || 0));
                  
                  // 3. The "most recent" is the latest processed one.
                  const mostRecent = processedFiles[0];
                  const historyFiles = processedFiles.slice(1);
                  const expanded = expandedDocs[file.id] || false;

                  // 4. Determine the fulfillment status for each link that requires this file.
                  const pendingLinkInfo = (cliente.links || [])
                    .map((linkId) => {
                      const link = linksData[linkId];
                      if (!link || !link.files?.[file.id]?.required) {
                        return null;
                      }

                      const filesForType = link.files[file.id]?.fileNames || [];
                      
                      // Requirement is fulfilled if any file is processed.
                      const isFulfilled = filesForType.some(f => f.status === 'procesado');
                      if (isFulfilled) return null;

                      // If not fulfilled, find the status of the latest attempt.
                      const latestFile = filesForType.sort((a, b) => (b.uploadedAt || 0) - (a.uploadedAt || 0))[0];

                      if (!latestFile) {
                        return { link, status: 'awaiting_upload' };
                      }

                      if (latestFile.status === 'error') {
                        return { link, status: 'error', error: latestFile.error, fileName: latestFile.name };
                      }

                      if (['subido', 'procesando'].includes(latestFile.status)) {
                        return { link, status: 'in_progress' };
                      }
                      
                      return { link, status: 'awaiting_upload' }; // Fallback
                    })
                    .filter(Boolean)[0];

                  // 5. Compute document expiry status from most recent processed data
                  const expedienteExpiryStatus = mostRecent?.results
                    ? computeDocumentExpiryStatus(file.id, mostRecent.results)
                    : null;

                  return (
                    <div key={file.id} className="bg-white rounded-xl p-4 border border-azul-surelia/20 flex flex-col h-full shadow-lg hover:shadow-xl transition-shadow">
                      <div className="flex items-center justify-between mb-2">
                        <Text className="font-bold text-base text-azul-surelia">{file.label}</Text>
                        {/* Botón de acciones (tres puntos) */}
                        <div className="relative" ref={el => { actionMenuRefs.current[file.id] = el; }}>
                          <button
                            className="flex items-center justify-center p-2 rounded-lg border border-azul-surelia/30 hover:bg-azul-surelia/10 focus:outline-none transition-colors"
                            title="Acciones"
                            type="button"
                            onClick={() => setExpandedDocs(prev => ({ ...prev, [file.id + '_menu']: !prev[file.id + '_menu'] }))}
                          >
                            <EllipsisVerticalIcon className="w-5 h-5 text-azul-surelia" />
                          </button>
                          {/* Menú de acciones secundarias */}
                          {expandedDocs[file.id + '_menu'] && (
                            <div className="absolute z-20 right-0 mt-2 w-48 bg-white border border-azul-surelia/30 rounded-lg shadow-xl py-1">
                              {/* Abrir archivo en nueva pestaña */}
                              <button
                                className="w-full text-left px-4 py-2 text-sm hover:bg-azul-surelia/10 flex items-center gap-2 text-gray-700 hover:text-azul-surelia transition-colors"
                                onClick={async () => {
                                  setExpandedDocs(prev => ({ ...prev, [file.id + '_menu']: false }));
                                  if (mostRecent && mostRecent.path) {
                                    try {
                                      let url = downloadUrlsRef.current[mostRecent.path];
                                      if (!url) {
                                        const fileRef = ref(storage, mostRecent.path);
                                        url = await getDownloadURL(fileRef);
                                        downloadUrlsRef.current[mostRecent.path] = url;
                                      }
                                      window.open(url, '_blank');
                                    } catch (e) {
                                      alert('No se pudo abrir el archivo.');
                                    }
                                  }
                                }}
                                disabled={!mostRecent || !mostRecent.path}
                                title="Abrir archivo"
                                type="button"
                              >
                                <span>Abrir archivo</span>
                              </button>
                              {/* Ver resultados */}
                              <button
                                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                                onClick={async () => {
                                  setExpandedDocs(prev => ({ ...prev, [file.id + '_menu']: false }));
                                  if (mostRecent && mostRecent.path) {
                                    let url = downloadUrlsRef.current[mostRecent.path];
                                    if (!url) {
                                      try {
                                        const fileRef = ref(storage, mostRecent.path);
                                        url = await getDownloadURL(fileRef);
                                        downloadUrlsRef.current[mostRecent.path] = url;
                                      } catch (e) {
                                        alert('No se pudo obtener el archivo para ver resultados.');
                                        return;
                                      }
                                    }
                                    
                                    setProcessedViewData({
                                      processedFileData: {
                                        ...mostRecent,
                                        fileDocId: mostRecent.id // Use full ID as fileDocId
                                      }, // The whole file document
                                      fileType: file.id,
                                      fileName: mostRecent.name,
                                      fileLabel: file.label,
                                      pdfUrl: url,
                                      linkId: mostRecent.linkId, // Add linkId for review functionality
                                    });
                                    setIsProcessedViewOpen(true);
                                  }
                                }}
                                disabled={!mostRecent || !mostRecent.path}
                              >
                                Ver resultados
                              </button>
                              {/* Solicitar actualización */}
                              <button
                                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                                onClick={() => {
                                  setExpandedDocs(prev => ({ ...prev, [file.id + '_menu']: false }));
                                  handleOpenFileDialogWithSelection([file.id]);
                                }}
                              >
                                Solicitar actualización
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Link indicator */}
                      {mostRecent && (
                        <div className="mb-3">
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-azul-surelia/10 text-azul-surelia text-xs font-medium" title={`Link: ${mostRecent.linkName}`}>
                            {mostRecent.linkName.length > 20 ? mostRecent.linkName.slice(0, 20) + '...' : mostRecent.linkName}
                          </span>
                        </div>
                      )}
                      
                      {/* Cotejado indicator */}
                      {mostRecent && (
                        <div className="mb-2">
                          {mostRecent?.reviewedBy ? (
                            <div className="flex flex-col gap-1">
                              <span 
                                className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-verde-lima/20 text-azul-surelia text-xs font-semibold w-fit border border-verde-lima/30"
                                title={`Revisado ${mostRecent?.reviewedAt ? 'el ' + new Date(mostRecent.reviewedAt.seconds * 1000).toLocaleDateString("es-MX") : ''} por ${mostRecent.reviewedBy}`}
                              >
                                ✓ Revisado
                              </span>
                              <span className="text-xs text-zinc-600">
                                por {mostRecent.reviewedBy}
                                {mostRecent?.reviewedAt && (
                                  <span className="block">
                                    el {new Date(mostRecent.reviewedAt.seconds * 1000).toLocaleDateString("es-MX", { 
                                      year: "numeric", 
                                      month: "short", 
                                      day: "numeric" 
                                    })}
                                  </span>
                                )}
                              </span>
                            </div>
                          ) : (
                            <span 
                              className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-red-100 text-red-700 text-xs font-semibold w-fit border border-red-200"
                              title="No revisado"
                            >
                              ✗ No revisado
                            </span>
                          )}
                        </div>
                      )}
                      {/* Badge de vigencia del documento */}
                      {expedienteExpiryStatus && expedienteExpiryStatus.status === 'expired' && (
                        <div className="mb-2">
                          <span
                            className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-red-100 text-red-700 text-xs font-semibold w-fit border border-red-200"
                            title={expedienteExpiryStatus.message}
                          >
                            Vencido
                          </span>
                        </div>
                      )}
                      {expedienteExpiryStatus && expedienteExpiryStatus.status === 'warning' && (
                        <div className="mb-2">
                          <span
                            className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold w-fit border border-amber-200"
                            title={expedienteExpiryStatus.message}
                          >
                            Por vencer
                          </span>
                        </div>
                      )}
                      {/* Badge de estado de solicitud */}
                      {pendingLinkInfo?.status === 'awaiting_upload' && (
                        <div className="mb-2 flex items-center gap-2">
                          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-xs font-semibold border border-orange-200" title={`Esperando archivo en: ${pendingLinkInfo.link.name || pendingLinkInfo.link.id}`}>Pendiente de Carga</span>
                        </div>
                      )}
                      {pendingLinkInfo?.status === 'in_progress' && (
                        <div className="mb-2 flex items-center gap-2 text-xs text-yellow-700 bg-yellow-50 rounded-full px-3 py-1 border border-yellow-200 font-semibold">
                          <ExclamationTriangleIcon className="w-4 h-4 text-yellow-600" />
                          En Proceso
                        </div>
                      )}
                      {pendingLinkInfo?.status === 'error' && (
                        <div className="flex flex-col gap-1 text-xs text-red-700 bg-red-50 rounded-lg px-3 py-2 border border-red-200">
                          <div className="flex items-center gap-2 font-medium">
                            <ExclamationTriangleIcon className="w-4 h-4 text-red-600" />
                            <span>Error al procesar</span>
                          </div>
                          <span className="truncate" title={pendingLinkInfo.fileName}>{pendingLinkInfo.fileName}</span>
                          {pendingLinkInfo.error && <span className="text-red-600">Motivo: {pendingLinkInfo.error}</span>}
                        </div>
                      )}
                      {mostRecent && (
                        <div className="flex items-center gap-2 mb-2">
                          <button
                            className="p-1 rounded hover:bg-blue-100 transition"
                            title="Abrir archivo"
                            onClick={async () => {
                              if (mostRecent && mostRecent.path) {
                                try {
                                  let url = downloadUrlsRef.current[mostRecent.path];
                                  if (!url) {
                                    const fileRef = ref(storage, mostRecent.path);
                                    url = await getDownloadURL(fileRef);
                                    downloadUrlsRef.current[mostRecent.path] = url;
                                  }
                                  window.open(url, '_blank');
                                } catch (e) {
                                  alert('No se pudo abrir el archivo.');
                                }
                              }
                            }}
                            type="button"
                          >
                            <ArrowTopRightOnSquareIcon className="w-5 h-5 text-blue-600" />
                          </button>
                          <span className="text-xs text-gray-700 truncate max-w-[120px]" title={mostRecent.name}>{mostRecent.name}</span>
                          <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs" title="Fecha de actualización">
                            {mostRecent.uploadedAt ? new Date(mostRecent.uploadedAt).toLocaleDateString("es-MX", { year: "numeric", month: "short", day: "numeric" }) : ''}
                          </span>
                        </div>
                      )}
                      {historyFiles.length > 0 && (
                        <button
                          className="text-xs text-blue-600 underline self-start mb-1"
                          onClick={() => setExpandedDocs(prev => ({ ...prev, [file.id]: !expanded }))}
                          type="button"
                        >
                          {expanded ? 'Ocultar historial' : `Ver historial (${historyFiles.length})`}
                        </button>
                      )}
                      {expanded && historyFiles.length > 0 && (
                        <div className="mt-1 border-t pt-2 space-y-2">
                          {historyFiles.map((f, idx) => (
                            <div key={f.path + idx} className="flex items-center gap-2 text-xs">
                              <button
                                className="p-1 rounded hover:bg-blue-100 transition"
                                title="Abrir archivo"
                                onClick={async () => {
                                  if (f && f.path) {
                                    try {
                                      let url = downloadUrlsRef.current[f.path];
                                      if (!url) {
                                        const fileRef = ref(storage, f.path);
                                        url = await getDownloadURL(fileRef);
                                        downloadUrlsRef.current[f.path] = url;
                                      }
                                      window.open(url, '_blank');
                                    } catch (e) {
                                      alert('No se pudo abrir el archivo.');
                                    }
                                  }
                                }}
                                type="button"
                              >
                                <ArrowTopRightOnSquareIcon className="w-4 h-4 text-blue-600" />
                              </button>
                              <span className="truncate max-w-[120px]" title={f.name}>{f.name}</span>
                               <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 max-w-[150px] truncate" title={`Link: ${f.linkName}`}>{f.linkName}</span>
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-600" title="Fecha de actualización">
                                {f.uploadedAt ? new Date(f.uploadedAt).toLocaleDateString("es-MX", { year: "numeric", month: "short", day: "numeric" }) : ''}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Historial de links generados */}
            <div className="mt-8">
              <Text className="font-medium mb-2">Historial de links de solicitud</Text>
                <div className="space-y-3">
                {(cliente.links && cliente.links.length > 0) ? (
                  [...cliente.links]
                    .sort((a, b) => {
                      const linkA = linksData[a];
                      const linkB = linksData[b];
                      if (!linkA || !linkB) return 0;
                      return (linkB.createdAt?.toDate?.() || 0) - (linkA.createdAt?.toDate?.() || 0);
                    })
                    .map((linkId, index) => {
                      const link = linksData[linkId];
                      const requiredPills = link?.files
                        ? REQUIRED_FILES
                            .filter((f) => link.files[f.id]?.required)
                            .map((f) => ({
                              id: f.id,
                              fullLabel: f.label,
                              shortLabel: REQUIRED_FILE_SHORT_LABELS[f.id] || f.label,
                              status: getLinkFileStatus(link.files[f.id]?.fileNames),
                            }))
                        : [];
                      const entregadoCount = requiredPills.filter((p) => p.status === "procesado").length;
                      return (
                      <div
                        key={linkId}
                        className="flex items-start justify-between p-3 bg-zinc-50 rounded-lg hover:bg-zinc-100 hover:cursor-pointer transition-colors gap-3"
                        onClick={() => navigate(`/app/expedientes/${linkId}`)}
                      >
                        <div className="flex items-start gap-3 min-w-0 flex-1">
                          <div
                            className={`w-2 h-2 rounded-full flex-shrink-0 mt-1.5 ${
                              requiredPills.length > 0 && entregadoCount === requiredPills.length
                                ? "bg-green-500"
                                : "bg-orange-500"
                            }`}
                            title={
                              requiredPills.length === 0
                                ? "Sin documentos requeridos"
                                : entregadoCount === requiredPills.length
                                  ? "Todos los documentos entregados"
                                  : `${entregadoCount}/${requiredPills.length} entregados`
                            }
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Text className="font-medium truncate" title={link?.name || `Link #${index + 1}`}>
                                {link?.name || `Link #${index + 1}`}
                              </Text>
                              {requiredPills.length > 0 && (
                                <span className="text-xs text-zinc-500 flex-shrink-0">
                                  {entregadoCount}/{requiredPills.length} entregados
                                </span>
                              )}
                            </div>
                            <Text className="text-xs text-zinc-500">
                              {link?.createdAt
                                ?.toDate()
                                .toLocaleDateString("es-MX", {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                })}
                            </Text>
                            {requiredPills.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1.5">
                                {requiredPills.map((p) => {
                                  const style = LINK_PILL_STYLES[p.status];
                                  return (
                                    <span
                                      key={p.id}
                                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${style.cls}`}
                                      title={`${p.fullLabel} — ${LINK_PILL_STATUS_LABEL[p.status]}`}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <span aria-hidden="true">{style.icon}</span>
                                      <span>{p.shortLabel}</span>
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 relative flex-shrink-0">
                          {/* Copiar link */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopyLink(linkId);
                            }}
                            className="p-2 text-zinc-500 hover:text-zinc-900 transition-colors relative"
                            onMouseEnter={() => setHoveredTooltip(`copy-${linkId}`)}
                            onMouseLeave={() => setHoveredTooltip(null)}
                            aria-label="Copiar link para el cliente"
                          >
                            <ClipboardDocumentIcon
                              className={`h-5 w-5 ${
                                copiedLinkId === linkId ? "text-green-600" : ""
                              }`}
                            />
                            {hoveredTooltip === `copy-${linkId}` && (
                              <span className="absolute left-1/2 -translate-x-1/2 -top-10 bg-zinc-800 text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap z-20">
                                Copiar link para el cliente
                              </span>
                            )}
                          </button>
                          {/* Abrir link cliente */}
                          <a
                            href={`/expediente/${linkId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-zinc-500 hover:text-zinc-900 transition-colors relative"
                            onClick={(e) => e.stopPropagation()}
                            onMouseEnter={() => setHoveredTooltip(`open-${linkId}`)}
                            onMouseLeave={() => setHoveredTooltip(null)}
                            aria-label="Abrir link del cliente en nueva pestaña"
                          >
                            <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                            {hoveredTooltip === `open-${linkId}` && (
                              <span className="absolute left-1/2 -translate-x-1/2 -top-10 bg-zinc-800 text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap z-20">
                                Abrir link del cliente
                              </span>
                            )}
                          </a>
                          {/* Panel de control */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                              navigate(`/app/expedientes/${linkId}`);
                            }}
                            className="p-2 text-zinc-500 hover:text-zinc-900 transition-colors relative"
                            onMouseEnter={() => setHoveredTooltip(`panel-${linkId}`)}
                            onMouseLeave={() => setHoveredTooltip(null)}
                            aria-label="Abrir panel de control del link"
                          >
                            <DocumentTextIcon className="h-5 w-5" />
                            {hoveredTooltip === `panel-${linkId}` && (
                              <span className="absolute left-1/2 -translate-x-1/2 -top-10 bg-zinc-800 text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap z-20">
                                Panel de control del link
                              </span>
                            )}
                          </button>
                        </div>
                      </div>
                      );
                    })
                  ) : (
                    <Text variant="secondary" className="text-center py-4">
                      No hay links generados
                    </Text>
                  )}
                </div>
              <div className="flex justify-end mt-4">
                <Button color="white" onClick={() => handleOpenFileDialogWithSelection()} size="sm">
                  Nueva Solicitud
                </Button>
              </div>
              </div>
            </div>
          )}
        {activeTab === "contratos" && (
          <div className="p-4">
            <ClientContratosYFianzas
              clientId={clientId}
              clientData={cliente}
            />
          </div>
        )}
        {activeTab === "estadisticas" && (
          <div className="p-4">
            <ClientEstadisticas clientId={clientId} />
          </div>
        )}
        {activeTab === "afianzadoras" && (
          <div className="p-4">
            <ClientAfianzadoras
              clientId={clientId}
              clientData={cliente}
            />
          </div>
        )}
        {activeTab === "bitacora" && (
          <div className="p-4">
            <BitacoraTimeline filters={{ clientId }} />
          </div>
        )}
      </div>

      {/* Dialog para generar link */}
      <Dialog
        size="md"
        open={isFileDialogOpen}
        onClose={() => {
          setIsFileDialogOpen(false);
          setSelectedFiles({});
          setLinkName(""); // Reset link name when closing
        }}
      >
        <DialogTitle>Seleccionar Documentos</DialogTitle>

        <DialogBody>
          <div className="mb-5">
            <label className="text-sm font-medium text-azul-surelia block mb-1.5">
              Nombre del Link
            </label>
            <div className="flex gap-2">
              <Input
                value={linkName}
                onChange={(e) => setLinkName(e.target.value)}
                placeholder={`Link ${(cliente.links?.length || 0) + 1}`}
                className="flex-1"
              />
              <button
                type="button"
                onClick={() => setLinkName(getRandomLinkName())}
                className="px-2.5 rounded-lg border border-zinc-200 text-zinc-400 hover:text-azul-surelia hover:border-azul-surelia/30 hover:bg-azul-surelia/5 transition-colors"
                title="✨"
              >
                <ArrowPathIcon className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-azul-surelia">Documentos Requeridos</span>
              <div className="flex gap-1.5">
                <button
                  type="button"
                  onClick={() => setSelectedFiles({ contrato: true })}
                  className="px-2 py-1 text-xs font-medium rounded-md bg-azul-surelia/10 text-azul-surelia hover:bg-azul-surelia/20 transition-colors"
                >
                  Contrato
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedFiles({
                    constanciaSituacionFiscal: true,
                    acuseReciboDeclaracion: true,
                    estadosFinancierosExcel: true,
                    estadosFinancieros: true,
                    estadosFinancierosParciales: true,
                  })}
                  className="px-2 py-1 text-xs font-medium rounded-md bg-azul-surelia/10 text-azul-surelia hover:bg-azul-surelia/20 transition-colors"
                >
                  Actualización
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedFiles(
                    REQUIRED_FILES.reduce((acc, file) => ({ ...acc, [file.id]: true }), {})
                  )}
                  className="px-2 py-1 text-xs font-medium rounded-md bg-azul-surelia/10 text-azul-surelia hover:bg-azul-surelia/20 transition-colors"
                >
                  Todos
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedFiles({})}
                  className="px-2 py-1 text-xs font-medium rounded-md bg-zinc-100 text-zinc-600 hover:bg-zinc-200 transition-colors"
                >
                  Ninguno
                </button>
              </div>
            </div>
            <div className="border border-azul-surelia/15 rounded-lg overflow-hidden">
              <div className="grid grid-cols-2 divide-x divide-azul-surelia/10">
                <div className="divide-y divide-azul-surelia/10">
                  {REQUIRED_FILES.slice(0, Math.ceil(REQUIRED_FILES.length / 2)).map((file) => (
                    <div
                      key={file.id}
                      onClick={() => handleFileSelection(file.id)}
                      className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-azul-surelia/5 transition-colors"
                    >
                      <span onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedFiles[file.id] || false}
                          onChange={() => handleFileSelection(file.id)}
                        />
                      </span>
                      <span className="text-sm text-zinc-700">{file.label}</span>
                    </div>
                  ))}
                </div>
                <div className="divide-y divide-azul-surelia/10">
                  {REQUIRED_FILES.slice(Math.ceil(REQUIRED_FILES.length / 2)).map((file) => (
                    <div
                      key={file.id}
                      onClick={() => handleFileSelection(file.id)}
                      className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-azul-surelia/5 transition-colors"
                    >
                      <span onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedFiles[file.id] || false}
                          onChange={() => handleFileSelection(file.id)}
                        />
                      </span>
                      <span className="text-sm text-zinc-700">{file.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </DialogBody>

        <DialogActions>
          <Button
            color="white"
            onClick={() => {
              setIsFileDialogOpen(false);
              setSelectedFiles({});
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleGenerateLink}
            disabled={
              Object.values(selectedFiles).filter(Boolean).length === 0
            }
          >
            Generar Link
          </Button>
        </DialogActions>
      </Dialog>
{/*       <ProcessedFileDialog
        open={isProcessedDialogOpen}
        onClose={() => setIsProcessedDialogOpen(false)}
        processedFileData={processedDialogData.processedFileData}
        fileType={processedDialogData.fileType}
        fileName={processedDialogData.fileName}
        fileLabel={processedDialogData.fileLabel}
        pdfUrl={processedDialogData.pdfUrl}
        linkId={processedDialogData.linkId}
        onMarkAsReviewed={handleMarkAsReviewed}
        onUnmarkAsReviewed={handleUnmarkAsReviewed}
        onRefresh={fetchClienteAndLinks}
      /> */}

      {/* New Processed File View (full page slide-in) */}
      <ProcessedFileView
        isOpen={isProcessedViewOpen}
        onClose={() => setIsProcessedViewOpen(false)}
        processedFileData={processedViewData.processedFileData}
        fileType={processedViewData.fileType}
        fileName={processedViewData.fileName}
        fileLabel={processedViewData.fileLabel}
        pdfUrl={processedViewData.pdfUrl}
        linkId={processedViewData.linkId}
        onMarkAsReviewed={handleMarkAsReviewed}
        onUnmarkAsReviewed={handleUnmarkAsReviewed}
        onRefresh={fetchClienteAndLinks}
        clientId={clientId}
        clientName={cliente?.nombreEmpresa}
      />

      {/* Contract Chat Dialog */}
      <ContractChatDialog
        open={isChatDialogOpen}
        onClose={() => {
          setIsChatDialogOpen(false);
          setSelectedContract(null);
        }}
        contract={selectedContract}
      />

      {/* Custom Confirmation Dialog - Surelia Style */}
      <Dialog
        open={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, message: '', onConfirm: null, title: '' })}
      >
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-shrink-0 w-12 h-12 bg-azul-surelia/10 rounded-full flex items-center justify-center">
              <ExclamationTriangleIcon className="h-6 w-6 text-azul-surelia" />
            </div>
            <div>
              <Heading level={3} className="text-azul-surelia">{confirmDialog.title}</Heading>
            </div>
          </div>
          <Text className="text-gray-700 mb-6 whitespace-pre-line">
            {confirmDialog.message}
          </Text>
          <div className="flex justify-end gap-3">
            <Button
              color="white"
              onClick={() => setConfirmDialog({ isOpen: false, message: '', onConfirm: null, title: '' })}
            >
              Cancelar
            </Button>
            <Button
              color="surelia"
              onClick={() => {
                if (confirmDialog.onConfirm) {
                  confirmDialog.onConfirm();
                }
              }}
            >
              Confirmar
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Upload Fianza Dialog */}
      {uploadFianzaContractId && (
        <UploadFianza
          isOpen={isUploadFianzaOpen}
          onClose={() => {
            setIsUploadFianzaOpen(false);
            setUploadFianzaContractId(null);
          }}
          clientId={clientId}
          linkId={uploadFianzaContractId.linkId}
          contratoId={uploadFianzaContractId.id}
          pendingFianzas={uploadFianzaContractId.pendingFianzas || []}
          onSuccess={() => {
            fetchClienteAndLinks();
          }}
        />
      )}

      {/* Fianza View */}
      {selectedFianzaView && (
        <FianzaView
          isOpen={isFianzaViewOpen}
          onClose={() => {
            setIsFianzaViewOpen(false);
            setSelectedFianzaView(null);
          }}
          fianza={selectedFianzaView.fianza}
          clientData={cliente}
          contratoData={selectedFianzaView.contrato}
          onUpdate={async () => {
            await fetchClienteAndLinks();
          }}
        />
      )}

      {/* User Assignment Dialog */}
      <Dialog
        open={isAssignmentDialogOpen}
        onClose={() => setIsAssignmentDialogOpen(false)}
        size="md"
      >
        <div className="p-1">
          <Heading level={3} className="text-xl font-bold text-azul-surelia mb-4">
            Asignar Responsables
          </Heading>
          <UserAssignmentSelector
            assignedUsers={cliente?.assignedUsers || []}
            onSave={async (userIds) => {
              await updateClientAssignedUsers(clientId, userIds);
              // Update local state immediately
              setCliente(prev => ({ ...prev, assignedUsers: userIds }));
              setIsAssignmentDialogOpen(false);
            }}
            onCancel={() => setIsAssignmentDialogOpen(false)}
          />
        </div>
      </Dialog>

      {/* Invite Client User Dialog */}
      <InviteClientUserDialog
        isOpen={isInviteClientUserOpen}
        onClose={() => setIsInviteClientUserOpen(false)}
        clientsList={clientsList || []}
        preSelectedClientId={clientId}
        onCreated={async ({ nombre, correo, clientIds: cIds }) => {
          try {
            const clientNames = cIds.map((id) => clientsList.find((c) => c.id === id)?.nombreEmpresa || id).join(', ');
            await logBitacora({
              action: 'CREATE',
              collection: 'usuarios',
              documentId: correo,
              metadata: {
                clientId,
                clientName: cliente?.nombreEmpresa || 'Sin nombre',
                description: `Creó cuenta de cliente "${nombre}" (${correo}) con acceso a: ${clientNames}`,
              },
              user,
              userData,
            });
          } catch (logErr) {
            console.error('Failed to log client user creation:', logErr);
          }
        }}
      />
    </div>
  );
};

export default ClienteDetails;
